import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { JSX } from 'react'

import type { GridState } from '@lensmira/ui'
import { branchDate } from '@lensmira/domain'
import { resolveLocale, translate, type MessageKey, type Params } from '@lensmira/i18n'

import {
  readCommissionSchemes,
  readCurrentCashSession,
  readDispense,
  readJob,
  readLoanFrame,
  readOverdueLoanFrames,
  readLensSelection,
  readNotifications,
  readRecalls,
  readMessageTemplates,
  readRefractions,
  readPromotions,
  readMessages,
  readPurchaseOrder,
  readPurchaseOrders,
  readShipment,
  readShipmentOrder,
  readShipmentOrders,
  readRepairs,
  deleteDirect,
  postDirect,
  readCatalogueFrames,
  readCatalogueLenses,
  readCatalogueTreatments,
  readFrameImageBytes,
  readFrameImages,
  readLensTreatments,
  readPriceBooks,
  readStockLocations,
  readTrialExpiry,
  readSuppliers,
  readStockMovements,
  readStockPositions,
  readTransfersInTransit,
  readQuote,
  readReceipt,
  readPriceImpact,
  readReport,
  readReportListing,
  readMiraQuota,
  readOrder,
  readOrders,
  readOpenDispenses,
  readSessionContext,
  readCustomerDetail,
  readCorrectionOffers,
  readCorrections,
  readCustomerMatches,
  readFramesForSale,
  readHolds,
  readStaff,
  readTerminals,
  readClosedPeriods,
  readErasurePlan,
  readOnboarding,
  readSatisfactionDue,
  readSupportAccess,
  readWorkQueue,
  ReadFailed,
  readJobTicket,
  readPrescriptionCopy,
  readAppointmentClosures,
  readAppointmentSchedule,
  readExpenses,
  readAged,
  readClaimCapture,
  readPanel,
  readPanels,
  readPanelStatements,
  readTenantPolicies,
  readAppointmentHoursOverrides,
  readAppointmentResources,
  readAppointmentSlots,
  readAppointmentTypes,
  readBookable,
  readBranchDay,
  readBranchGroups,
  readClProducts,
  readRecallRules,
  readSupplierPrices,
  readVisitDates,
  type JobTicketData,
  type PrescriptionCopyData,
  type BranchContext,
  type ReadsConfig,
  type SessionContext,
  type SupplierRow,
} from './api/reads.js'
import { BlockedWrites } from './BlockedWrites.js'
import { QueueFlag } from './queue/QueueFlag.js'
import { applyDensity, loadDensity, storeDensity, type Density } from './density.js'
import { LocaleProvider } from './i18n.js'
import { MiraSurface, type MiraContext } from './mira/MiraSurface.js'
import { IndexedDbQueueStorage } from './queue/indexeddb-storage.js'
import { WriteQueue } from './queue/queue.js'
import { httpTransport } from './queue/transport.js'
import { WriteQueueProvider } from './queue/use-write-queue.js'

import { Shell } from './Shell.js'
import { CashSession, type CashSessionSummary } from './screens/CashSession.js'
import {
  CustomerDetail,
  type CustomerDetailData,
  type PrescriptionVersionView,
} from './screens/CustomerDetail.js'
import { CustomerSearch, type CustomerMatch } from './screens/CustomerSearch.js'
import { NewCustomer } from './screens/NewCustomer.js'
import { CustomerErasure } from './screens/CustomerErasure.js'
import { CustomerExport } from './screens/CustomerExport.js'
import { CustomerPortalLink } from './screens/CustomerPortalLink.js'
import { Fittings } from './screens/Fittings.js'
import { Refractions } from './screens/Refractions.js'
import { PeriodClose } from './screens/PeriodClose.js'
import { Feedback } from './screens/Feedback.js'
import { ShopSetup } from './screens/ShopSetup.js'
import { Suppliers } from './screens/Suppliers.js'
import { Dispensing } from './screens/Dispensing.js'
import { JobDetail, type JobDetailData } from './screens/JobDetail.js'
import { NewOrder } from './screens/NewOrder.js'
import { Corrections } from './screens/Corrections.js'
import { OrderDetail, type OrderDetailData } from './screens/OrderDetail.js'
import { OrderList } from './screens/OrderList.js'
import { Quote, type QuoteData } from './screens/Quote.js'
import { Receipt, type ReceiptData } from './screens/Receipt.js'
import { Notifications, type NotificationTab } from './screens/Notifications.js'
import { Recalls } from './screens/Recalls.js'
/**
 * The seven screens of this round, every one of them a panel inside a destination that already
 * exists. `decisions/0040`: all twenty-six rail glyphs are taken, `rail.test.ts` asserts it as
 * data, and the diary is the first genuine new destination to be answered with a panel instead.
 */
import { AppointmentCalendar } from './screens/AppointmentCalendar.js'
import { AppointmentHours } from './screens/AppointmentHours.js'
import { Expenses, type ExpenseView } from './screens/Expenses.js'
import { TenantPolicies } from './screens/TenantPolicies.js'
import { AppointmentOffers } from './screens/AppointmentOffers.js'
import { ClCatalogue } from './screens/ClCatalogue.js'
import { CustomerSharing } from './screens/CustomerSharing.js'
import { Diary, type SlotQuery } from './screens/Diary.js'
import { RecallRules } from './screens/RecallRules.js'
import { PriceListDenied, SupplierPrices } from './screens/SupplierPrices.js'
import { MessageTemplates } from './screens/MessageTemplates.js'
import { ApplyOffers } from './screens/ApplyOffers.js'
import { Outbox } from './screens/Outbox.js'
import { Promotions } from './screens/Promotions.js'
import { offerDownload } from './download.js'
import { CommissionSchemes } from './screens/CommissionSchemes.js'
import { DataFiles } from './screens/DataFiles.js'
import { SendToLab, type SendToLabRx } from './screens/SendToLab.js'
import { Stock } from './screens/Stock.js'
import { Transfers } from './screens/Transfers.js'
import { VoidSale } from './screens/VoidSale.js'
import { Purchasing } from './screens/Purchasing.js'
import { Shipments } from './screens/Shipments.js'
import { RaisePurchaseOrder } from './screens/RaisePurchaseOrder.js'
import { SupplierInvoice } from './screens/SupplierInvoice.js'
import { SendMessage } from './screens/SendMessage.js'
import { Repairs } from './screens/Repairs.js'
import { LoanFrames } from './screens/LoanFrames.js'
import { PriceImpact } from './screens/PriceImpact.js'
import { ClaimCapture } from './screens/ClaimCapture.js'
import { PanelAccounts } from './screens/PanelAccounts.js'
import { PanelRemittance } from './screens/PanelRemittance.js'
import { PanelStatements } from './screens/PanelStatements.js'
import { Reports } from './screens/Reports.js'
import { ReturnLine } from './screens/ReturnLine.js'
import { ClRxActions } from './screens/ClRxActions.js'
import type { ContactLensPrescriptionView } from './screens/ContactLensSection.js'
import { CustomerHolds } from './screens/CustomerHolds.js'
import { RxActions } from './screens/RxActions.js'
import { RxCapture } from './screens/RxCapture.js'
import { FrameCatalogue } from './screens/FrameCatalogue.js'
import { LensCatalogue } from './screens/LensCatalogue.js'
import { FrameImages } from './screens/FrameImages.js'
import { LensTreatments } from './screens/LensTreatments.js'
import { PriceBooks } from './screens/PriceBooks.js'
import { TreatmentCatalogue } from './screens/TreatmentCatalogue.js'
import { JobTicket } from './screens/JobTicket.js'
import { PrescriptionCopy } from './screens/PrescriptionCopy.js'
import { SignIn } from './screens/SignIn.js'
import { SignUp } from './screens/SignUp.js'
import { Onboarding } from './screens/Onboarding.js'
import { Satisfaction } from './screens/Satisfaction.js'
import { SupportAccess } from './screens/SupportAccess.js'
import { WorkQueue } from './screens/WorkQueue.js'
import { TakePayment } from './screens/TakePayment.js'
import {
  clearSession,
  enrolTerminal,
  enrolTotp,
  forgetTerminalToken,
  loadSession,
  loadTerminalToken,
  storeTerminalToken,
  type Session,
} from './session/session.js'
import { Terminals } from './screens/Terminals.js'

/**
 * The counter shell.
 *
 * `DESIGN-TOKENS.md` §16.3's shell: a left rail on desktop, a bottom bar on a counter tablet, and
 * the branch and work-queue count visible from every screen.
 *
 * **The tenant is resolved once, here, from the session, and carried explicitly.** `CLAUDE.md`:
 * "Tenant context is resolved once per request and carried explicitly. Never a global." That
 * discipline matters more on the client than on the server, where a global at least dies with the
 * request: here it would survive a route change and a user switching shops.
 *
 * **Nothing on this screen is guessed.** The branch, its currency, its timezone, its terminals and
 * its invoice series all come from `GET /auth/context`, and where a shop has configured nothing the
 * shell says so and refuses rather than filling the gap in. A counter that invented a currency or a
 * rounding rule would be making shop policy, and every figure downstream would carry the guess.
 */

/** Seven days, from `decisions/0005`. Stated here rather than defaulted inside the queue. */
const IDEMPOTENCY_RETENTION_MS = 7 * 24 * 60 * 60 * 1000

/**
 * Same origin, so the dev server and production both work without a build-time variable.
 * `vite.config.ts` proxies `/api` to the API in development.
 */
const API_BASE_URL = '/api'

type SectionId =
  | 'queue'
  | 'suppliers-setup'
  | 'frame-catalogue'
  | 'period-close'
  | 'customers'
  | 'new-order'
  | 'dispensing'
  | 'orders'
  | 'cash'
  | 'reports'
  | 'notifications'
  | 'recalls'
  | 'repairs'
  | 'loan-frames'
  | 'purchasing'
  | 'raise-order'
  | 'supplier-invoices'
  | 'outbox'
  | 'templates'
  | 'commission'
  | 'data-files'
  | 'setup'
  | 'transfers'
  | 'promotions'
  | 'stock'
  | 'shipments'

/**
 * Only sections that exist and that this staff member may use.
 *
 * §16.3 describes a fuller rail. A destination that opens an empty frame is worse than an absent
 * one: it teaches the counter that half the buttons do not work. Each entry names the Appendix B
 * permission its screen needs, and the rail is filtered by the permission list the context returned,
 * so a cashier is not shown a section whose every action would 403.
 *
 * The server checks permissions on every endpoint regardless. This is about not offering a door
 * that is locked, not about locking it.
 */
/**
 * The rail's six sections, `decisions/0031`.
 *
 * **Twenty-six destinations in one flat list is not a navigation model.** Photographed at 1280 the
 * rail ran past the fold, each row prefixed by a single capital letter, so finding "Loan frames"
 * meant reading twenty-six labels. The sections are named for what a shop does rather than for how
 * this repository is organised, because that is the question somebody at a counter is actually
 * asking, and six groups of two to six is scannable in a way that twenty-six is not.
 *
 * **The grouping carries the meaning the icons were going to carry.** `OPEN.md` §3.2 lists the
 * fourteen domain icons as work that must be drawn and is not ours, and most destinations map to
 * one of them, so an icon-led rail stays blocked. The groups remain useful once they arrive.
 *
 * Headings, not controls. A shopkeeper who collapses a section and forgets is worse off than one
 * who scrolls, so none of them collapses.
 */
export type RailGroup = 'counter' | 'aftercare' | 'stock' | 'catalogue' | 'money' | 'shop'

export const RAIL_GROUPS: readonly { readonly id: RailGroup; readonly label: MessageKey }[] = [
  { id: 'counter', label: 'nav.group.counter' },
  { id: 'aftercare', label: 'nav.group.aftercare' },
  { id: 'stock', label: 'nav.group.stock' },
  { id: 'catalogue', label: 'nav.group.catalogue' },
  { id: 'money', label: 'nav.group.money' },
  { id: 'shop', label: 'nav.group.shop' },
]

/**
 * What the mobile bottom bar carries, in bar order. `decisions/0031`.
 *
 * These five are the counter loop, done a few times an hour. Everything else is one tap further
 * away behind More, which is the right cost for something done a few times a day. §16.3 asks for
 * "bottom bar on mobile", and a bottom bar is not twenty-six items in a horizontal scroller, which
 * is what 390px was showing.
 *
 * It is also the set `packages/i18n` already had Urdu for, which was not planned and is the
 * strongest evidence available that these are the five.
 */
export const BOTTOM_BAR: readonly SectionId[] = [
  'queue',
  'customers',
  'new-order',
  'orders',
  'cash',
]

/**
 * The rail, as data, so it can be checked as data.
 *
 * Exported for `rail.test.ts` and nothing else. Two slices written the same day both picked the
 * glyph `I`, each having read the list before the other landed, and the shop would have had two
 * rail buttons showing one letter. Nothing could see it: every suite passed, because a glyph is
 * `aria-hidden` and no test asserted on the set as a whole.
 */
export const SECTIONS: readonly {
  readonly id: SectionId
  /** The catalogue key for the label, resolved at render so the rail follows the language. */
  readonly label: MessageKey
  /**
   * Still a Latin initial in every language. Real icons are `OPEN.md` §3.2, outstanding, and
   * `IC-02` says an icon never carries meaning alone, so the glyph is `aria-hidden` and the label
   * beside it is what is read. In Urdu the initial no longer matches the word it sits beside.
   */
  readonly glyph: string
  /** Which of `RAIL_GROUPS` it sits in. A union, so an ungrouped destination does not compile. */
  readonly group: RailGroup
  readonly permission: string
  /** A second row that also opens the section, for a screen with two halves. */
  readonly orPermission?: string
  /**
   * A second row that must also be held, for a screen whose first read takes one row and whose
   * second takes another. Where `orPermission` opens the section for either row, this needs both.
   */
  readonly andPermission?: string
}[] = [
  // First, because §16.3 makes it "the default landing surface for staff".
  {
    id: 'queue',
    label: 'nav.queue',
    glyph: 'Q',
    group: 'counter',
    permission: 'report_operational',
  },
  {
    id: 'customers',
    label: 'nav.customers',
    glyph: 'C',
    group: 'counter',
    permission: 'customer_view',
  },
  {
    id: 'new-order',
    label: 'nav.newOrder',
    glyph: 'N',
    group: 'counter',
    permission: 'dispense_create',
  },
  // Next to the order form rather than near the work queue: what it holds is sales that were
  // started on that form and not finished, so it is where someone goes when a customer comes back.
  {
    id: 'dispensing',
    label: 'nav.dispensing',
    glyph: 'U',
    group: 'counter',
    permission: 'dispense_create',
  },
  {
    id: 'orders',
    label: 'nav.orders',
    glyph: 'O',
    group: 'counter',
    permission: 'report_operational',
  },
  {
    id: 'cash',
    label: 'nav.cash',
    glyph: 'R',
    group: 'counter',
    permission: 'cash_session_open_close',
  },
  /**
   * Last, because it is the one section nobody stands at the counter to use.
   *
   * `report_operational`, which Appendix B gives every role, and the listing itself is filtered by
   * role: `RPT-11` makes a report's class a rule over its columns, so a dispenser is offered the
   * operational ones and an owner both. Gating the rail on `report_financial` would hide the
   * day-to-day reports from the people who need them.
   */
  {
    id: 'reports',
    label: 'nav.reports',
    glyph: 'Z',
    group: 'money',
    permission: 'report_operational',
  },
  /**
   * `PRC-012`: `price_book_view`, the row `GET /price-books/versions/:id/simulation` requires.
   * Seeing the effect of a draft is not publishing it, which is `price_book_edit_publish`, the
   * owner alone. Nothing on the screen writes, so there is no second row to gate on.
   */
  /**
   * `MSG-005a`: the in-app channel, which is one of the two v1 has. Same `report_operational` as
   * the work queue, because what it lists is operational signal about this branch rather than
   * anybody's private message, and the server already narrows it to `staff_id is null or me`.
   */
  {
    id: 'notifications',
    label: 'nav.notifications',
    glyph: 'A',
    group: 'aftercare',
    permission: 'report_operational',
  },
  /** `APT-004`: `recall_run_campaign`, the permission the endpoints themselves require. */
  {
    id: 'recalls',
    label: 'nav.recalls',
    glyph: 'B',
    group: 'aftercare',
    permission: 'recall_run_campaign',
  },
  /** `REP-001`: `repair_intake_complete`, the one repair row Appendix B has. */
  {
    id: 'repairs',
    label: 'nav.repairs',
    glyph: 'P',
    group: 'aftercare',
    permission: 'repair_intake_complete',
  },
  /**
   * `REP-005`: `loan_frame_issue`, Appendix B's "Loan frame · issue" (owner, manager, dispenser,
   * optician). Every read and every write on the screen takes that row except one.
   *
   * No `orPermission`. The write-off takes `stock_adjust`, which Appendix B gives the owner and the
   * manager and marks sensitive, and both of them already hold `loan_frame_issue`, so there is
   * nobody the second row would let in. It is gated inside the screen as `canWriteOff` instead,
   * because a button offered to the dispenser who may lend a frame but may not forfeit a deposit is
   * a 403 that stops the whole queue, which is what `purchasing` learned at a loading dock.
   */
  {
    id: 'loan-frames',
    label: 'nav.loanFrames',
    glyph: 'L',
    group: 'aftercare',
    permission: 'loan_frame_issue',
  },
  /**
   * `SUP-001`: `purchase_order_raise`, which the list endpoint itself requires.
   *
   * Booking a delivery in needs `stock_receive` instead, which a dispenser has and an accountant
   * does not, so the two halves of this screen are not one permission. The rail is gated on the
   * list, because a person who cannot see what is on order has nothing to open; the receipt panel
   * is what the server refuses if they may not use it.
   */
  /**
   * Two rows, because the screen has two halves and Appendix B keeps them apart.
   *
   * `purchase_order_raise` is owner, manager and accountant; `stock_receive` is owner, manager and
   * dispenser. Gated on the first alone, the dispenser who stands at the dock could not open the
   * screen at all, and the accountant was offered a Book in button whose write came back 403 and
   * stopped the queue. So the section opens for either row and each half is gated on its own.
   */
  {
    id: 'purchasing',
    group: 'stock',
    label: 'nav.purchasing',
    glyph: 'S',
    permission: 'purchase_order_raise',
    orPermission: 'stock_receive',
  },
  /**
   * `SUP-001`: `purchase_order_raise`, Appendix B's "Purchase order - raise" (owner, manager and
   * accountant), which is the row `POST /purchase-orders` itself requires.
   *
   * Separate from the `purchasing` section rather than a third half of it. That screen is the
   * counter's: a list of what is outstanding and a panel for booking a delivery in, gated on
   * `purchase_order_raise` or `stock_receive`. Raising an order is neither of those jobs, it is a
   * long form nobody fills in at a counter with a customer waiting, and its own entry is what lets
   * the dispenser keep the delivery panel without being shown a form every write of which they
   * would be refused.
   */
  {
    id: 'raise-order',
    label: 'nav.raiseOrder',
    glyph: 'V',
    group: 'stock',
    permission: 'purchase_order_raise',
  },
  /**
   * The supplier list itself. `purchase_order_raise` or `stock_receive`, the pair
   * `GET /suppliers` takes: the writes on the screen are gated separately on the first, because a
   * dispenser at the dock needs to know who sent a delivery and has no reason to rename anybody.
   *
   * Separate from `purchasing` (the orders) rather than a panel on it: one answers "where is my
   * delivery" and the other "who do we buy from", and they are opened on different days.
   */
  /**
   * §6.4 `CAT-001` to `CAT-003`, `CAT-031`. Appendix B's `Catalogue - edit`, owner and manager.
   *
   * **A shop could be seeded with two frames and never add a third.** `insert into frame_model`
   * appeared once outside tests, in `tools/seed`, so all six tenants on the deployment held zero
   * frame rows: `FrameChoice` reads the catalogue and every walkthrough of a sale ran against
   * seeded rows, which is why nobody noticed there was no way to make more.
   *
   * Its own section rather than a panel on Setup, because Setup is about people and branches and
   * this is opened by whoever is unpacking a delivery of frames.
   */
  {
    id: 'frame-catalogue',
    group: 'catalogue',
    label: 'nav.frameCatalogue',
    // `J`, the only letter left: `K` was taken and `rail.test.ts` caught it, which is the third
    // time that guard has found a collision rather than a reviewer finding it.
    glyph: 'J',
    permission: 'catalogue_edit',
  },
  {
    id: 'suppliers-setup',
    group: 'catalogue',
    label: 'nav.suppliersSetup',
    // `W` was taken. The glyph is `aria-hidden` and `IC-02` says an icon never carries meaning
    // alone, so what matters is only that no two rail buttons show the same letter, which
    // `rail.test.ts` checks as data after two slices once took the same one.
    glyph: 'H',
    permission: 'purchase_order_raise',
    orPermission: 'stock_receive',
  },
  /**
   * `INV-006`: `supplier_invoice_match`, Appendix B's "Supplier invoice - match" (owner, manager
   * and accountant, not sensitive), which is the row both invoice routes require.
   *
   * Two things this gate does not cover, deliberately. The read it drives,
   * `GET /purchase-orders/:id`, takes `purchase_order_raise`, and Appendix B gives both rows to the
   * same three roles today, so the section cannot open onto a read that 403s; if the matrix ever
   * splits them this has to become both rows rather than either. And closing a query is
   * `purchase_order_approve`, owner and manager only, which is passed to the screen as a prop
   * rather than put in the gate: an accountant enters invoices all day and closes none of them.
   */
  {
    id: 'supplier-invoices',
    group: 'stock',
    label: 'nav.supplierInvoices',
    glyph: 'I',
    permission: 'supplier_invoice_match',
    // Both rows, not either: the screen's first read is `GET /purchase-orders/:id`, which takes
    // the second one. Appendix B gives both to the same three roles today, so a shop with default
    // grants sees no difference, and a shop that splits them gets no section rather than a section
    // that opens onto a 403.
    andPermission: 'purchase_order_raise',
  },
  /**
   * `MSG-005`: `message_send_manual`, the row every message route answers to.
   *
   * Beside the staff notifications rather than near the customer record, because what this screen
   * is for is the queue and the failures rather than one customer: `MSG-005`'s "a silent failure
   * means an uncollected job" is a thing somebody checks, not a thing they happen upon.
   */
  {
    id: 'outbox',
    label: 'nav.outbox',
    glyph: 'M',
    group: 'shop',
    permission: 'message_send_manual',
  },
  /**
   * `MSG-002`: `tenant_settings_policies`, which Appendix B gives the owner alone and marks
   * sensitive. What goes out under the shop's name to every customer is policy, and a shop with no
   * templates can send nothing at all, so this is the first settings screen the counter has.
   */
  {
    id: 'templates',
    label: 'nav.templates',
    glyph: 'W',
    group: 'shop',
    permission: 'tenant_settings_policies',
  },
  /**
   * `COM-001` to `COM-007`: `tenant_settings_policies`, and the permission is a judgement call
   * that this screen inherits rather than makes.
   *
   * Appendix B has 63 rows, two of them about commission, and neither is about **writing** a
   * scheme: "Commission - view own" is a staff member reading their own statement, and
   * "Commission - view all, approve" is the owner or accountant approving a period. All four
   * endpoints behind this screen require `tenant_settings_policies` instead, and
   * `commission.controller.ts` records why: §17 puts commission schemes under the counter's
   * settings, the matrix's settings row is "Tenant settings, policies" (owner only, marked
   * sensitive), and a scheme decides what staff are paid. A price book and a promotion each have
   * their own row; how staff are paid has none.
   *
   * So the rail is gated on the row the server actually checks, which keeps the door and the lock
   * the same shape. Nothing here invents a row: `later_todo.md` 6 already carries the nine missing
   * Appendix B rows as a product call, and a tenth would be the same answer.
   *
   * One permission for the whole section, unlike `purchasing`: both halves of this screen are the
   * same row, so there is no half to gate separately.
   */
  {
    id: 'commission',
    label: 'nav.commission',
    glyph: 'Y',
    group: 'money',
    permission: 'tenant_settings_policies',
  },
  /**
   * `MIG-001` and `MIG-005`. Either row, not both: `catalogue_bulk_import` brings a file in and
   * `customer_export` takes a copy out, and a shop can reasonably grant one without the other.
   *
   * `orPermission` rather than `andPermission` because the screen has two halves and shows only
   * the half the reader holds, so neither door answers 403. The accountant who may export and not
   * import gets the export and a sentence saying who can import.
   */
  /**
   * `INV-004`. `stock_view`, which every role holds, because what is on the road is what is about
   * to be on a shelf and the list is the answer to "where is my stock".
   *
   * The two writes take `stock_transfer_out_in` and the screen takes that as a prop: a cashier can
   * see what is coming and cannot send it, which is one screen rather than two and no door that
   * answers 403.
   */
  { id: 'transfers', label: 'nav.transfers', glyph: 'T', group: 'stock', permission: 'stock_view' },
  {
    id: 'data-files',
    group: 'shop',
    label: 'nav.dataFiles',
    glyph: 'D',
    permission: 'catalogue_bulk_import',
    orPermission: 'customer_export',
  },
  /**
   * `PRM-001`: `promotion_create_edit`, Appendix B's "Promotion - create, edit" (owner and
   * manager). Applying one at the till is a different row, `discount_apply_within_limit`, which is
   * why this section is about writing offers rather than taking them.
   */
  {
    id: 'promotions',
    label: 'nav.promotions',
    glyph: 'F',
    group: 'money',
    permission: 'promotion_create_edit',
  },
  /**
   * `TEN-002a`. `staff_create_assign_role` is Appendix B's own row for hiring, which it gives to
   * the owner and the manager. The branch panel inside the screen takes `branch_create_close`,
   * which is the owner alone, so a manager sees the half they may use rather than a door that
   * answers 403.
   */
  {
    id: 'setup',
    label: 'nav.setup',
    glyph: 'E',
    group: 'shop',
    permission: 'staff_create_assign_role',
  },
  /**
   * `ACC-008`'s period close. Appendix B's `Period - close`, which is the owner and the
   * accountant: locking a month is a books decision and the accountant is the one who makes it.
   */
  {
    id: 'period-close',
    label: 'nav.periodClose',
    glyph: 'X',
    group: 'money',
    permission: 'period_close',
  },
  /**
   * `INV-001`: `stock_view`, which Appendix B gives every role, because what is on the shelf is
   * what a cashier is asked about at the counter. Receiving, adjusting and counting take rows that
   * fewer roles hold, and they are not on this screen.
   */
  { id: 'stock', label: 'nav.stock', glyph: 'K', group: 'stock', permission: 'stock_view' },
  /**
   * `IMP-001`: two rows, and both are required.
   *
   * `landed_cost_allocate` is Appendix B's "Landed cost - allocate" row (owner, manager,
   * accountant), and `GET /shipments/:shipmentId` takes it. `purchase_order_raise` is what the two
   * order reads take, `GET /purchase-orders` and `GET /purchase-orders/:purchaseOrderId`, and those
   * are the only way to a shipment id (there is no `GET /shipments`) and to the receipt line ids an
   * allocation names. A person holding only one of the two would be offered a section that
   * answers 403 to its first read, which is the empty frame this list exists to avoid.
   *
   * Appendix B gives both rows to the same three roles, so a shop with default grants sees no
   * difference. The server checks the granted rows rather than the role, which is why this is an
   * `andPermission` and not an `orPermission`: either row alone is not enough to use the screen.
   * The screen still gates its ordering half on its own prop.
   */
  {
    id: 'shipments',
    group: 'stock',
    label: 'nav.shipments',
    glyph: 'G',
    permission: 'landed_cost_allocate',
    andPermission: 'purchase_order_raise',
  },
]

export interface AppProps {
  /**
   * The transport, injectable so a test can drive the whole shell against a fake server.
   *
   * Not a convenience. Every defect in `PROGRESS.md` problem 111 lived in the wiring **between** a
   * screen and its parent: the screen worked, the read worked, the write worked, and the callback
   * fired at the wrong moment. A component test that renders one screen with props cannot see any of
   * it, because supplying the prop is the thing nobody does in the running app. The only test that
   * can is one that mounts this file with the real queue and a fake `fetch`, so this is what makes
   * that test possible.
   */
  readonly doFetch?: typeof globalThis.fetch
  /**
   * `LOC-002`: the language to use until the server can say what this person's is. Before sign-in
   * nothing identifies the person, so this is the only source there; after it, a language the
   * server sends for the staff member or the shop takes over (`resolveLocale`'s order). Tests set it
   * to put the whole shell in Urdu.
   */
  readonly language?: string
}

export function App(props: AppProps = {}): JSX.Element {
  const { doFetch, language } = props
  const fetchImpl = useMemo(() => doFetch ?? globalThis.fetch.bind(globalThis), [doFetch])
  const [session, setSession] = useState<Session | null>(() => loadSession())
  /** Which of the two unauthenticated screens is showing. `OPS-201`. */
  const [signingUp, setSigningUp] = useState(false)
  const [context, setContext] = useState<SessionContext | null>(null)
  const [contextError, setContextError] = useState<string | null>(null)
  const [branchId, setBranchId] = useState<string | null>(null)
  const [terminalId, setTerminalId] = useState<string | null>(null)

  const [section, setSection] = useState<SectionId>('queue')
  const [customer, setCustomer] = useState<CustomerMatch | null>(null)
  /**
   * The prescription captured for this customer in this session, carried to the order.
   *
   * `NewOrder` declares `prescriptionId` and documents it as "captured on an earlier screen; this
   * screen only carries it forward", and nothing ever passed it. So every order this counter has
   * ever raised was written with `prescription_id` NULL, the New order screen said "No prescription
   * attached yet" over a prescription that existed, and a sale type that needs one could not be
   * completed at all. `RX-001` and `JOB-002`: the job freezes the Rx it was made from, and an order
   * that carries none has nothing to freeze.
   *
   * Cleared with the customer, because a prescription belongs to a person and carrying it to the
   * next sale would attach one customer's eyes to another's glasses.
   */
  const [prescriptionId, setPrescriptionId] = useState<string | null>(null)
  /**
   * Whether the Customers section is capturing a prescription, as opposed to searching.
   *
   * Separate from `customer`, because `customer` had been doing both jobs: it named the person this
   * sale is for **and** decided whether the Customers section showed the Rx grid, so the only way
   * back to the search box was to forget the customer. Once the prescription started being carried
   * forward, that produced an order with one person's eyes and no name on it, which is worse than
   * either half was alone: `RX-001` makes a prescription belong to somebody.
   */
  const [capturingRx, setCapturingRx] = useState(false)
  /**
   * Prescription values Mira read and a person then accepted field by field (`AI-001`), waiting to be
   * looked at on the capture grid. A prefill: nothing is saved until the dispenser presses Save, and
   * it goes with the customer it was read for, never to the next one.
   */
  const [rxDraft, setRxDraft] = useState<{
    readonly grid: GridState
    readonly serial: number
  } | null>(null)
  /**
   * Bumped when stock is received or corrected, which re-keys the stock screen and re-reads it.
   *
   * The same shape as the orders epoch and for the same reason: `Stock` loads in effects keyed on
   * its props, so an imperative refresh would mean handing it a loader that changes identity, and
   * `OrderList` records what that cost (a new closure every render, and the list flashing its
   * loading line back over itself).
   */
  const [stockEpoch, setStockEpoch] = useState(0)
  const [openOrder, setOpenOrder] = useState<OrderDetailData | null>(null)
  /**
   * Which payment on the open order a correction panel is pointed at. §6.33 `COR-001`.
   *
   * `payment:wrong_staff` is the fourth of `COR-001`'s four supported combinations and the only
   * one with nowhere to be reached from: `corrections.service.ts` has supported it since it was
   * written, `GET /corrections/offers` answers for it, and no screen ever passed `'payment'`.
   *
   * An id rather than the row, because the row is re-read with the order and holding a copy here
   * would let the panel send a version the screen is no longer showing.
   */
  const [openPayment, setOpenPayment] = useState<string | null>(null)
  /**
   * The prescription the open order was made against, for `JOB-002`'s snapshot.
   *
   * Loaded here rather than inside the panel, like every other read in this shell: a screen that
   * fetches for itself is a screen a test can only drive by mocking a module. Null means the sale
   * has no customer, or no prescription, or the reader does not hold `prescription_view`, and the
   * panel says so rather than hiding the form, because the server takes values and not an id.
   */
  const [openOrderRx, setOpenOrderRx] = useState<SendToLabRx | 'loading' | null>('loading')
  const [openJob, setOpenJob] = useState<JobDetailData | null>(null)
  /**
   * The job ticket, once somebody asks for one. `JOB-011`, Appendix L, `decisions/0015`.
   *
   * Its own read rather than a field on `JobDetailData`, because a ticket is a document assembled
   * from six tables and nobody wants it on every job open. Fetched when the button is pressed.
   */
  const [openTicket, setOpenTicket] = useState<JobTicketData | null>(null)
  /**
   * The prescription copy, once somebody asks for one. `RXW-003`, Appendix L.
   *
   * Its own read rather than a field on the customer record, because the sheet is assembled from
   * six tables and nobody wants it on every customer open.
   */
  const [openRxCopy, setOpenRxCopy] = useState<PrescriptionCopyData | null>(null)
  const [rxCopyError, setRxCopyError] = useState<string | null>(null)
  const [ticketError, setTicketError] = useState<string | null>(null)
  const [openQuote, setOpenQuote] = useState<QuoteData | null>(null)
  const [openReceipt, setOpenReceipt] = useState<ReceiptData | null>(null)
  const [paying, setPaying] = useState(false)
  /**
   * Whether the open order is showing the returns flow.
   *
   * `ReturnLine` was built, tested and never imported, so `POS-005`'s return was unreachable from
   * the shell: the screen existed and no door led to it. Same shape as `paying`, including the
   * reason the button is conditional rather than the screen being conditional.
   */
  const [returning, setReturning] = useState(false)
  /**
   * The customer record, once one is opened. §6.2 makes it the hub a shopkeeper works from.
   *
   * `CustomerDetail.tsx` was written and tested and nothing imported it, so picking a customer went
   * straight to the Rx grid and the record could not be opened at all. Picking now opens the record
   * and capturing is a button on it, which is one more click to a prescription and the difference
   * between having a customer record and having a screen that renders one.
   */
  const [openCustomer, setOpenCustomer] = useState<CustomerDetailData | null>(null)
  const [customerError, setCustomerError] = useState<string | null>(null)
  /**
   * Which customer read is the current one, so a slower earlier one cannot win.
   *
   * Five call sites loaded a customer record and every one of them took whichever response arrived
   * last. On a shop's connection that is not theoretical: a dispenser taps the wrong row, taps the
   * right one, and if the first read resolves second the record panel shows the first customer
   * while `customer` is the second. The panel's balance, prescriptions and orders are then one
   * person's and the id `RxCapture` writes against is another's, so a prescription read off the
   * screen is stored on the wrong record and `JOB-002` freezes it into a job.
   *
   * A ticket rather than a cancellation flag per call site, because the five sites are in four
   * different closures and the thing that has to be true is global: only the most recent request
   * may set the record.
   */
  const customerRequest = useRef(0)
  const [cashSession, setCashSession] = useState<CashSessionSummary | null>(null)
  /**
   * Whether anybody has successfully answered "is the drawer open".
   *
   * Separate from `cashSession` because null has to mean one thing. The shell asserted a closed
   * drawer from a read that had never happened and from one that came back 403, which are both
   * "unknown" and neither is "closed".
   */
  const [drawerKnown, setDrawerKnown] = useState(false)
  /**
   * Which supplier's prices are open, and the supplier rows the interim chooser offers.
   *
   * **Declared up here with the rest of what a person brings to a tablet**, because it belongs in
   * `forgetSession` below and the list under it is every `useState` in this component read in
   * order. A price list left open would be the next person's first screen, past a door that is
   * absent for them. `ROLE-001a` makes a cost price the one field in this app withheld from a role
   * that can otherwise see everything, so it is the last state to leave lying about.
   */
  const [pricesFor, setPricesFor] = useState<SupplierRow | null>(null)
  /**
   * `BR-11`: what this branch has spent, held here because `Expenses` takes rows.
   *
   * **The one screen in this app that is handed its list instead of a loader**, which is the
   * handover it arrived with. The cost is that a failed expense read reaches the shell's error line
   * rather than the screen's own retry, unlike every other screen here. `../later_todo.md` records
   * it; the fix is a `loadExpenses` prop and is not worth rewriting a tested screen for today.
   */
  const [expenses, setExpenses] = useState<readonly ExpenseView[]>([])

  /**
   * Appendix B as this session holds it, read once and used by everything below.
   *
   * Declared here rather than beside the rail because the eager drawer load is above the rail and
   * needs it: a permission check that only the rail can see gates only the rail.
   */
  const permissions = new Set(context?.staff.permissions ?? [])
  const mayKnowTheDrawer = permissions.has('cash_session_open_close')
  /**
   * The rail's queue badge is the only other read that runs on every screen for everybody, and it
   * had the same hole as the drawer: a permission set without `report_operational` collected a 403
   * from `GET /work-queue` on every branch change, for a badge the rail would not show them
   * anyway. The counter's own sweep across five roles found exactly these two.
   */
  const mayReadTheQueue = permissions.has('report_operational')
  /**
   * `BR-11`'s `expenses_record`, as a boolean rather than the `permissions` set, which is rebuilt
   * every render and so cannot be a dependency.
   *
   * The read is gated for the drawer's reason: `GET /expenses` takes the same row as the write, so
   * asking without it collects a 403 on every branch change for a panel nobody is shown.
   */
  const mayRecordExpenses = permissions.has('expenses_record')
  /**
   * `ROLE-001a` as `priceListVisibleTo` applies it, which is the row **and** the grant.
   *
   * `SUP-002`'s whole surface answers 403 `COST_NOT_VISIBLE` unless the caller holds Appendix B's
   * `cost_price_view` and the per-branch cost grant at every branch they work in, so the role row
   * alone is not the question the door has to ask. The grant arrives on the context filtered to
   * this person's own branches, which is the same set the server compares against.
   *
   * An older server does not send the field at all, and that is read as "not told" rather than as
   * "not granted": offering the door and letting the screen's own wall explain is better than
   * hiding a price list from an owner who has every grant there is.
   */
  const costGranted = context?.staff.costVisibleBranchIds
  const mayOpenPriceList =
    permissions.has('cost_price_view') &&
    (costGranted === undefined ||
      (context?.branches ?? []).every((row) => costGranted.includes(row.id)))
  /**
   * `DESIGN-TOKENS.md` §5 ships both densities, and until this there was no way to reach one of
   * them. Read once from the device, applied to `<body>` where `tokens.css` looks for it.
   */
  const [density, setDensity] = useState<Density>(() => {
    const stored = loadDensity()
    applyDensity(stored)
    return stored
  })
  const changeDensity = useCallback((next: Density) => {
    setDensity(next)
    storeDensity(next)
    applyDensity(next)
  }, [])

  /**
   * §16.3: "Branch switcher and work-queue count always visible."
   *
   * Held here rather than inside the queue screen, because the whole point of the count is that
   * it is on screen while the shop is doing something else. A count you have to navigate to is a
   * count nobody sees, and `WRK-001` makes a job waiting on somebody the unit of work in a shop.
   */
  const [queueCount, setQueueCount] = useState<number | null>(null)
  const [screenError, setScreenError] = useState<string | null>(null)

  // `LOC-002`: the one place the language is decided. The staff member's own setting wins over the
  // shop's, which wins over English. `language` stands in for the person before they sign in.
  const locale = resolveLocale({
    user: context?.staff.language ?? language ?? null,
    tenant: context?.settings.language ?? null,
  })
  const t = (key: MessageKey, params?: Params): string => translate(locale, key, params)

  const reads: ReadsConfig | null = useMemo(
    () =>
      session
        ? {
            baseUrl: API_BASE_URL,
            tenantId: session.tenantId,
            token: session.token,
            doFetch: fetchImpl,
          }
        : null,
    [session, fetchImpl],
  )

  const queue = useMemo(() => {
    if (!session) return null
    return new WriteQueue({
      // The tenant is part of the storage name, so a shared tablet running two shops keeps two
      // queues rather than sending one shop's pending writes under the other's session.
      storage: new IndexedDbQueueStorage(session.tenantId),
      transport: httpTransport({
        baseUrl: API_BASE_URL,
        tenantId: session.tenantId,
        accessToken: () => session.token,
        fetch: fetchImpl,
      }),
      now: () => Date.now(),
      sentRetentionMs: IDEMPOTENCY_RETENTION_MS,
    })
  }, [session, fetchImpl])

  /**
   * The context is loaded once per session. Everything else on screen depends on it, so it is
   * fetched before any section renders rather than per screen, which would mean four screens each
   * discovering separately that the shop has no branches.
   *
   * A callback as well as an effect, because `ShopSetup` can now change what is in it: a branch
   * opened on that screen belongs in the branch chooser, and nothing else on screen knows how to
   * put it there.
   */
  /**
   * Everything that belonged to whoever was signed in, dropped in one place.
   *
   * **There are two ways out of a session and they must not differ.** The 401 branch below was
   * added first and cleared only the context and the token, which on a cold boot is
   * indistinguishable from this because the rest is already at its default. Mid-session it is not:
   * `refreshContext` also runs when a language changes and when `ShopSetup` opens a branch.
   *
   * **Then this one inherited `signOut`'s own gaps**, which was the same mistake one level up.
   * `signOut` had cleared the customer and the Rx draft since it was written and never the rest,
   * so the list below is every `useState` in this component read in order and sorted into two
   * kinds. What a tablet keeps, and what belongs to a person:
   *
   *  - **The branch and the terminal belong to the person.** `refreshContext` resolves them with
   *    `previous ?? loaded.branches[0]`, so a branch left standing is kept even when the new
   *    person's context does not list it. Every branch-scoped read then fails `BR-01`, and the
   *    topbar falls back to the tenant slug. Nulling them here is what lets the new context choose,
   *    and it is invisible in a dev shop where everybody is at the same branch.
   *  - **The open order and job belong to the person.** Effects keyed on
   *    `openOrder?.customerId` and `openOrder?.prescriptionId` run on every screen, so an order
   *    left open re-reads that customer and their prescription under the next person's token.
   *  - **The drawer belongs to the person.** Every `cashSessionId` spread tests
   *    `cashSession?.status === 'open'` and not `drawerKnown`, so a stale session would be handed
   *    to somebody who cannot even read it.
   *  - **The density belongs to the tablet**, not to anybody, and stays. So do the two epoch
   *    counters, which are nothing but a nudge to re-read.
   *
   * §8.1 makes a prescription special-category health data, which is what makes this a list to get
   * right rather than tidiness.
   *
   * The write queue is deliberately not in here. A pending prescription belongs to the shop rather
   * than to the session, and discarding it because somebody went home would lose a sale.
   */
  const forgetSession = useCallback((tenantId: string) => {
    clearSession(tenantId)
    setSession(null)
    setContext(null)
    setContextError(null)
    setBranchId(null)
    setTerminalId(null)
    setSection('queue')
    setCustomer(null)
    setPrescriptionId(null)
    setCapturingRx(false)
    setRxDraft(null)
    setOpenOrder(null)
    setOpenPayment(null)
    setOpenOrderRx('loading')
    setOpenJob(null)
    setOpenQuote(null)
    setOpenReceipt(null)
    setPaying(false)
    setReturning(false)
    setOpenCustomer(null)
    setCustomerError(null)
    setCashSession(null)
    setDrawerKnown(false)
    setQueueCount(null)
    setScreenError(null)
    setLanguagePending(false)
    setFilingCustomer(false)
    // `SUP-002` and `ROLE-001a`: a cost price is withheld per branch and per person, so an open
    // price list is the person's rather than the tablet's. The supplier list it used to be
    // declared beside went with the interim chooser; `Suppliers` holds its own rows and is
    // remounted on the branch.
    setPricesFor(null)
    // `BR-11`: another branch's costs, and a role without the row must not inherit a list read for
    // somebody who had it.
    setExpenses([])
  }, [])

  /**
   * Close the ticket when the job under it changes. `JOB-011`.
   *
   * **Without this, job A's ticket renders under job B.** Nothing reset it: Back sets `openJob` to
   * null and opening another job replaces it, and the ticket is separate state that outlived both.
   * A lab document showing the wrong job's powers next to the right job's heading is the worst
   * version of this defect, and it needs no network failure to produce: open a ticket, press Back,
   * open the next job.
   *
   * Keyed on the id rather than the object, so the re-read after an action (`onActionQueued` calls
   * `readJob` and sets a new object for the same job) leaves an open ticket alone.
   */
  useEffect(() => {
    setOpenTicket(null)
    setTicketError(null)
  }, [openJob?.id])

  /**
   * Close the prescription copy when the customer under it changes. `RXW-003`.
   *
   * The job ticket had exactly this defect and it was found by review rather than by a test: one
   * record's document outliving the record it belongs to, so job A's powers rendered under job B's
   * heading. A prescription copy is worse, because it carries a patient's name beside somebody
   * else's powers.
   */
  useEffect(() => {
    setOpenRxCopy(null)
    setRxCopyError(null)
  }, [openCustomer?.id])

  const refreshContext = useCallback(async () => {
    if (!reads) return
    try {
      const loaded = await readSessionContext(reads)
      setContext(loaded)
      setBranchId((previous) => previous ?? loaded.branches[0]?.id ?? null)
      setTerminalId((previous) => previous ?? loaded.branches[0]?.terminals[0]?.terminalId ?? null)
    } catch (error: unknown) {
      /**
       * **A 401 here means the stored token is dead, and the shell must not pretend otherwise.**
       *
       * A tablet left on overnight boots with yesterday's token. The context read answered 401,
       * `contextError` showed "Sign in again.", and the shell rendered around it: the topbar said
       * "Signed in" over the tenant slug, the badge said "Drawer closed", the rail was empty, and
       * the only button on the page was "Sign out". The one instruction on screen was the one
       * thing the screen gave no way to do. That is a shop's morning, not an edge case.
       *
       * Only 401, by status rather than by code. A 503 from the tenant directory says in terms
       * that it is not the password and to try shortly, and signing somebody out over a directory
       * blip would be the worse failure; an outage never reaches here at all, because the write
       * queue's transport throws rather than answering.
       */
      if (error instanceof ReadFailed && error.status === 401 && session !== null) {
        forgetSession(session.tenantId)
        return
      }
      setContextError(error instanceof ReadFailed ? error.message : t('app.settingsLoadFailed'))
    }
    /**
     * **`t` is deliberately not a dependency**, and leaving it in cost an afternoon's confidence:
     * `t` here is a plain arrow in the component body, not `useTranslation`'s memoised one, so it
     * is a new function on every render. With it in the list this callback changed identity every
     * render, the effect below re-ran, `setContext` rendered again, and the whole shell spun.
     * `shell.test.tsx` and `app-refresh.test.tsx` hung rather than failed, which is what that looks
     * like from outside.
     *
     * The effect this replaced kept `t` out of its own list for the same reason. A stale `t` in a
     * fallback message is a locale behind for one render; a dependency on it is an infinite loop.
     */
  }, [reads, session, forgetSession])

  /**
   * Enrol an authenticator on the signed-in person's own account. `TEN-005`, `decisions/0014`.
   *
   * **Through `enrolTotp` and not through the write queue**, which every other write in this shell
   * uses. The queue exists so a sale survives a dead network (`OFF-001a`), and it is the wrong
   * carrier for this one: the secret is returned exactly once, nothing can read it back, and a
   * queued enrolment draining minutes later would show it to a screen nobody is watching while
   * leaving the account enrolled with a secret nobody has.
   *
   * The route is the unauthenticated one the sign-in screen uses, because it checks the password
   * itself and that is the re-auth `TEN-005` asks for on a change like this. The identifier comes
   * from the session context, so nobody retypes their own address.
   */
  const enrolOwnTotp = useCallback(
    async (password: string) => {
      const identifier = context?.staff.loginIdentifier
      if (identifier === undefined) return { kind: 'unavailable' as const }
      const result = await enrolTotp(API_BASE_URL, { identifier, password }, fetchImpl)
      switch (result.kind) {
        case 'enrolled':
          // Re-read, so the panel stops offering an enrolment the account now has.
          void refreshContext()
          return { kind: 'enrolled' as const, secret: result.secret, uri: result.uri }
        case 'already_enrolled':
          return { kind: 'already_enrolled' as const }
        case 'unavailable':
          return { kind: 'unavailable' as const }
        default:
          return { kind: 'rejected' as const }
      }
    },
    [context?.staff.loginIdentifier, fetchImpl, refreshContext],
  )

  useEffect(() => {
    void refreshContext()
  }, [refreshContext])

  const [languagePending, setLanguagePending] = useState(false)
  /**
   * `LOC-002`'s per-user half, which nothing in the product could set.
   *
   * `staff.language` has been in the schema since it was written, `session-context.service.ts`
   * selects it and `resolveLocale` prefers it over the shop's. No route and no screen ever set it,
   * so a dispenser in a Lahore shop could not put the counter into Urdu however complete the
   * catalogue got.
   *
   * Through the write queue like every other write (`OFF-001a`), so a language chosen while the
   * connection is down lands when it comes back rather than being lost. Then the context is
   * re-read rather than patched: the server is the one that knows which of `LOC-002`'s three
   * states this person is now in, because "as the shop is set" resolves through a setting this
   * screen does not own.
   *
   * Not kept locally beside the density. `LOC-002` is about the person and not the device: a
   * dispenser who signs in on the other till should still be reading Urdu.
   */
  const changeLanguage = useCallback(
    (next: string) => {
      if (!queue) return
      setLanguagePending(true)
      void (async () => {
        try {
          await queue.enqueue({
            method: 'PATCH',
            path: '/staff/me/language',
            label: t('shell.languageQueued'),
            // The empty string is "as the shop is set", which the route reads as null.
            body: { language: next === '' ? null : next },
          })
          /**
           * Drained here, the way `WriteQueueProvider`'s own `enqueue` wrapper does it.
           *
           * `queue.enqueue` persists and emits; it does not send. Without this the write waited
           * for the five-second tick, so the shell stayed in the old language for several seconds
           * after somebody chose a new one, which reads as the control not working.
           *
           * And the re-read waits for the drain rather than the enqueue: if the write is refused
           * the context is unchanged, which is the truth, and `BlockedWrites` is what says so.
           */
          await queue.drain()
          await refreshContext()
        } finally {
          setLanguagePending(false)
        }
      })()
    },
    // `t` is deliberately absent, for the reason `refreshContext` above states: it is a plain
    // arrow recreated every render, and a dependency on it spins the shell. There is no
    // exhaustive-deps rule configured in this workspace to disable, so this is a comment and not a
    // directive: the omission is the decision, and it is written down where it was made.
    [queue, refreshContext],
  )

  const branch: BranchContext | null =
    context?.branches.find((candidate) => candidate.id === branchId) ?? null
  // Pulled out as a string so a loader needing the branch's own day depends on that rather than on
  // the object, which `find` hands back fresh whenever the context is re-read.
  const branchTimezone = branch?.timezone ?? null

  // Bumped to make `OrderList` re-run its own load. The list owns its rows, so refreshing means
  // asking it to fetch again rather than keeping a second copy here that could disagree with it.
  const [ordersEpoch, setOrdersEpoch] = useState(0)
  const refreshOrders = useCallback(() => {
    setOrdersEpoch((n) => n + 1)
  }, [])

  /**
   * `quiet` is for the eager load below, which runs on every screen.
   *
   * A failed drawer read while somebody is capturing a prescription is not that screen's error and
   * must not take over its error line. On the cash screen itself it is exactly that screen's error,
   * so the explicit refreshes stay loud.
   */
  const refreshCashSession = useCallback(
    async (quiet = false) => {
      if (!reads || !branchId || !terminalId) return
      /**
       * **Appendix B first, then the read.** This load runs on every screen for everybody, and
       * `cash_session_open_close` is a cashier, dispenser, manager and owner row: an optician's
       * boot fired it and collected a 403, four times over with the dev double-render. The server
       * was right every time. Asking a question you may not have the answer to, on every boot, is
       * the shape of defect `later_todo.md` 31 was: a door opened to the wrong role.
       */
      if (!mayKnowTheDrawer) return
      if (!quiet) setScreenError(null)
      try {
        setCashSession(await readCurrentCashSession(reads, branchId, terminalId))
        setDrawerKnown(true)
      } catch (error) {
        if (quiet) return
        setScreenError(error instanceof ReadFailed ? error.message : t('app.drawerLoadFailed'))
      }
    },
    [reads, branchId, terminalId, mayKnowTheDrawer],
  )

  /**
   * **Loaded as soon as the branch and terminal are known, not when the cash screen is opened.**
   *
   * This used to be `if (section === 'cash')`, so `cashSession` stayed null everywhere else and
   * two things were wrong at once on every other screen. The shell badge read "Drawer closed" over
   * an open drawer. And `TakePayment` is handed a `cashSessionId` only when the session is known to
   * be open, so a cash payment was refused with "Cash has to go into an open drawer. Open a cash
   * session for this terminal first", which is a true rule and a false instruction: there was one
   * open, and the cashier was being told to do something already done. Walking the counter against
   * a seeded shop is what found it; nothing in jsdom notices, because no test navigates between
   * sections and then pays.
   */
  useEffect(() => {
    void refreshCashSession(true)
  }, [refreshCashSession])

  /**
   * `BR-11`: the branch's recorded costs, re-read on a branch change and after a save lands.
   *
   * Not section scoped, like the drawer above and for a smaller version of the same reason: the
   * panel sits under the cash drawer, and a list that loads only once that section is open shows an
   * empty list for a moment on a panel whose whole content is the list.
   */
  const refreshExpenses = useCallback(async () => {
    if (!reads || !branchId || !mayRecordExpenses) return
    try {
      setExpenses(await readExpenses(reads, branchId))
    } catch (error) {
      setScreenError(error instanceof ReadFailed ? error.message : t('app.expensesLoadFailed'))
    }
  }, [reads, branchId, mayRecordExpenses])

  useEffect(() => {
    void refreshExpenses()
  }, [refreshExpenses])

  /**
   * `OPS-204`. Stable, because `Onboarding` reads it in an effect.
   *
   * Not branch-scoped: the checklist is the shop's and lives in the platform database, so it is
   * the same six items whichever branch somebody is standing at.
   */
  const loadOnboarding = useCallback(async () => {
    if (!reads) return { items: [], done: 0, total: 0 }
    return readOnboarding(reads)
  }, [reads])

  /**
   * `OPS-207`. Stable, because `Satisfaction` reads it in an effect. Not branch-scoped: the question
   * is the owner's about the product, and the answer is recorded against the shop.
   */
  const loadSatisfaction = useCallback(async () => {
    if (!reads) return { due: false, min: 0, max: 0, commentMaxLength: 0 }
    return readSatisfactionDue(reads)
  }, [reads])

  // Stable across renders, so `WorkQueue`'s load effect does not re-run every time this component
  // re-renders. Its own body calls `setQueueCount`, so an inline arrow made a loop: fetch, set the
  // count, re-render the parent, new closure, fetch again, and the list flashed "Loading what is
  // waiting" back over itself. `OrderList` takes the same shape of prop and is re-keyed on an epoch
  // instead, which is why only this one span.
  const loadQueue = useCallback(async () => {
    if (!reads || !branchId) return []
    const groups = await readWorkQueue(reads, branchId)
    setQueueCount(groups.reduce((total, group) => total + group.items.length, 0))
    return groups
  }, [reads, branchId])

  // Loaded per branch rather than per visit to the queue, so the badge is populated before anyone
  // opens it. Silent on failure: a rail badge is not the place to report a network problem, and the
  // queue screen itself says so plainly when it cannot load.
  useEffect(() => {
    if (!reads || !branchId || !mayReadTheQueue) return
    let cancelled = false
    readWorkQueue(reads, branchId)
      .then((groups) => {
        if (cancelled) return
        setQueueCount(groups.reduce((total, group) => total + group.items.length, 0))
      })
      .catch(() => {
        if (!cancelled) setQueueCount(null)
      })
    return () => {
      cancelled = true
    }
  }, [reads, branchId, mayReadTheQueue])

  /**
   * The open order's prescription, keyed on the order's own fields rather than on the section.
   *
   * Every path that sets `openOrder` goes through this: the list click, and the re-read after a
   * payment, a return, a void, an offer or a job. An effect keyed on the section would miss the
   * re-reads, which is the defect `PROGRESS.md` problem 111 found four times over.
   *
   * The version the order names is the one that is wanted, not today's active one: `JOB-002`
   * freezes the Rx at order time, and a prescription corrected after the sale must not change what
   * the lab is told to make. `RX-002` keeps every version, so the id resolves even once superseded.
   */
  useEffect(() => {
    const customerId = openOrder?.customerId
    const prescriptionId = openOrder?.prescriptionId
    if (!reads || customerId === null || customerId === undefined) {
      // No customer is a settled answer, not a pending one: a walk-in sale has no Rx to wait for.
      setOpenOrderRx(null)
      return
    }
    let cancelled = false
    // Marked pending before the read, so opening a second order does not show the first order's
    // figures for as long as the fetch takes.
    setOpenOrderRx('loading')
    readCustomerDetail(reads, customerId)
      .then((customer) => {
        if (cancelled) return
        // Absent rather than empty means withheld (`ROLE-001a`): a reader without
        // `prescription_view` gets no `prescriptions` field at all, and the panel then says the
        // figures could not be read instead of showing an empty form as though there were no Rx.
        const versions = customer.prescriptions
        if (versions === undefined) {
          setOpenOrderRx(null)
          return
        }
        const version =
          prescriptionId === null || prescriptionId === undefined
            ? undefined
            : versions.find((candidate) => candidate.id === prescriptionId)
        /**
         * The most recent fitting, which is the **first** row.
         *
         * `customer-detail-read.service.ts` orders them `by f.measured_at desc` and
         * `readCustomerDetail` passes them through untouched, so `length - 1` took the oldest
         * fitting the customer ever had. A customer fitted twice would have had the measurements
         * from their previous frame frozen into a job, and `JOB-002` means a lab then makes
         * exactly that. Found by reading the service rather than the comment that used to sit
         * here, which said "most recent" over `length - 1`.
         *
         * Not matched to the frame on the sale: no read reaching here carries the order's frame
         * variant, which is the same gap as `later_todo.md` 17's lens spec.
         */
        const fitting = customer.fittings[0]
        if (version === undefined && fitting === undefined) {
          setOpenOrderRx(null)
          return
        }
        setOpenOrderRx({
          version: version?.version ?? 0,
          ...(version === undefined ? {} : { od: version.od, os: version.os }),
          ...(fitting === undefined
            ? {}
            : {
                monocularPdOd: fitting.monocularPdOd,
                monocularPdOs: fitting.monocularPdOs,
                ...(fitting.segHeightOd === undefined ? {} : { segHeightOd: fitting.segHeightOd }),
                ...(fitting.segHeightOs === undefined ? {} : { segHeightOs: fitting.segHeightOs }),
              }),
        })
      })
      .catch(() => {
        // Not shown as a screen error: the panel's own sentence covers it, and a failed Rx read
        // must not stop somebody looking at the order for any other reason.
        if (!cancelled) setOpenOrderRx(null)
      })
    return () => {
      cancelled = true
    }
  }, [reads, openOrder?.customerId, openOrder?.prescriptionId])

  /**
   * Whether the new-customer form is open. `CUS-001`.
   *
   * Closed by default and never a destination of its own: finding somebody is the common case and
   * filing a new record is what happens when the search comes back empty, so the form sits under
   * the search rather than beside it in the rail.
   */
  const [filingCustomer, setFilingCustomer] = useState(false)

  /**
   * Appendix K's matcher for `NewCustomer`'s `CUS-003` check, through the shell's one read path.
   *
   * Stable across renders, because the screen debounces on it: a new function identity every
   * render would restart the timer on every keystroke the parent re-rendered through.
   */
  /**
   * `RET-01`'s first step for `CustomerErasure`. Stable across renders, because the panel reads it
   * in an effect: a new identity each render would re-read the plan on every parent render.
   */
  const loadErasurePlan = useCallback(
    async (customerId: string) => {
      if (!reads) throw new Error('No session')
      return await readErasurePlan(reads, customerId)
    },
    [reads],
  )

  const findCustomerMatches = useCallback(
    async (query: string) => (reads ? await readCustomerMatches(reads, query) : []),
    [reads],
  )

  /**
   * Open a customer's record, discarding an answer that is no longer the one wanted.
   *
   * The ticket is what does the work: it drops a response that is no longer the one wanted, and
   * `app-refresh.test.tsx` drives the race that proves it, holding the first read back until the
   * second has answered.
   *
   * The id check beside it is reachable by no test, and stays. A response carries the record it is
   * for, so comparing it is free, and it is the half that still holds if a future caller builds its
   * own read and forgets the ticket. A guard that cannot be exercised sequentially is worth keeping
   * when what it prevents is a prescription stored against the wrong person.
   */
  const openCustomerById = useCallback(
    (customerId: string, report: (message: string) => void) => {
      if (!reads) return
      const ticket = customerRequest.current + 1
      customerRequest.current = ticket
      readCustomerDetail(reads, customerId)
        .then((loaded) => {
          if (ticket !== customerRequest.current) return
          if (loaded.id !== customerId) return
          setOpenCustomer(loaded)
        })
        .catch((error: unknown) => {
          if (ticket !== customerRequest.current) return
          /**
           * `CUSTOMER_ERASED` is not a read that failed. It is a record that is gone, and it
           * arrives here because the erasure panel re-reads the record it has just erased.
           *
           * Everything else leaves the record on screen, deliberately: losing somebody's history
           * because one read timed out is worse than a stale panel with an error under it. This
           * one is the opposite. The name was just deleted at the customer's own request, and the
           * only remaining copy of it is the one this screen is still rendering. `CUS-006` is not
           * honoured by a shop whose counter still shows the person they erased.
           */
          if (error instanceof ReadFailed && error.code === 'CUSTOMER_ERASED') {
            setOpenCustomer(null)
            setCustomer(null)
            // Both belonged to the person who is gone. A draft left behind would be submitted
            // against a record that no longer identifies anybody.
            setPrescriptionId(null)
            setRxDraft(null)
          }
          // Named, not swallowed: a record that will not open is something a shopkeeper has to be
          // able to report.
          report(error instanceof Error ? error.message : t('app.customerOpenFailed'))
        })
    },
    [reads, t],
  )

  /** `INV-004`: what is on the road to or from this branch. Stable, for `loadQueue`'s reason. */
  const loadTransfers = useCallback(async () => {
    if (!reads || !branchId) return []
    return readTransfersInTransit(reads, branchId)
  }, [reads, branchId])

  // Stable for the same reason `loadQueue` is: `Dispensing` loads in an effect keyed on the prop,
  // so an inline arrow is a new function every render and the list reloads forever.
  const loadOpenDispenses = useCallback(async () => {
    if (!reads || !branchId) return []
    return readOpenDispenses(reads, branchId)
  }, [reads, branchId])

  const loadDispenseById = useCallback(
    async (dispenseId: string) => {
      if (!reads) throw new Error('No session')
      return readDispense(reads, dispenseId)
    },
    [reads],
  )

  /**
   * `DSS-001`, for the lens chooser inside `NewOrder`. Stable for `loadQueue`'s reason: `LensChoice`
   * loads in an effect keyed on this prop, so an inline arrow reloads the catalogue for ever.
   *
   * `undefined` with no prescription attached, which is what makes `NewOrder` say so rather than
   * showing an empty chooser: there is nothing to rank against and no powers to price by.
   */
  const customerId = customer?.id
  const loadLensSelection = useCallback(async () => {
    if (!reads || !branchId || prescriptionId === null) {
      throw new Error('No prescription attached')
    }
    // `CAT-015` and `DSP-004`: the server checks cut-out only when it is told which fitting to
    // check against, and nothing else in the counter says so. The most recent one is the first row
    // (see the `openOrderRx` effect). A customer with none, or a read that fails, gets the list
    // without a cut-out check, which is what the server does with no measurement.
    let fittingMeasurementId: string | undefined
    if (customerId !== undefined) {
      try {
        fittingMeasurementId = (await readCustomerDetail(reads, customerId)).fittings[0]?.id
      } catch {
        fittingMeasurementId = undefined
      }
    }
    return readLensSelection(reads, {
      branchId,
      prescriptionId,
      ...(fittingMeasurementId === undefined ? {} : { fittingMeasurementId }),
      // The shop's own rounding rule. The `new-order` branch below refuses when it is null, so
      // this is never a direction chosen here.
      rounding: context?.settings.pricingRoundingMode ?? 'half_up',
    })
  }, [reads, branchId, prescriptionId, customerId, context?.settings.pricingRoundingMode])

  /** `ROLE-001`: who works here, for the setup screen. Stable, for `loadQueue`'s reason. */
  const loadClosedPeriods = useCallback(
    async () => (reads ? await readClosedPeriods(reads) : []),
    [reads],
  )

  const loadStaff = useCallback(async () => {
    if (!reads) return []
    return readStaff(reads)
  }, [reads])

  /** `TEN-006`: the counter terminals, for the owner's enrolment panel. Stable, like `loadStaff`. */
  const loadTerminals = useCallback(async () => {
    if (!reads) return []
    return readTerminals(reads)
  }, [reads])

  /**
   * `TEN-006`: whether this device holds a terminal token. State, so the panel re-renders when it
   * is stored or forgotten; the token itself lives in local storage and is never held here.
   */
  const [terminalHere, setTerminalHere] = useState(() => loadTerminalToken() !== null)

  /**
   * `COR-001`'s two reads for the open order. Stable, because `Corrections` loads both in an
   * effect keyed on them: a new function identity every render is an endless re-read.
   *
   * They throw rather than returning empty without a session. The panel renders a sentence saying
   * it could not check, which is the honest answer; an empty offer list would read as "nothing on
   * this record can be corrected", which is a different claim and a false one.
   */
  const loadCorrectionOffers = useCallback(async () => {
    if (!reads || openOrder === null) throw new Error('No session')
    return readCorrectionOffers(reads, 'sale', openOrder.id)
  }, [openOrder, reads])

  const loadCorrections = useCallback(async () => {
    if (!reads || openOrder === null) throw new Error('No session')
    return readCorrections(reads, 'sale', openOrder.id)
  }, [openOrder, reads])

  /**
   * The same two reads for a payment. The record type is the only thing that differs, which is
   * what `corrections.controller.ts` was built for: the counter names the record and the server
   * decides what may be corrected on it.
   */
  const loadPaymentCorrectionOffers = useCallback(async () => {
    if (!reads || openPayment === null) throw new Error('No session')
    return readCorrectionOffers(reads, 'payment', openPayment)
  }, [openPayment, reads])

  const loadPaymentCorrections = useCallback(async () => {
    if (!reads || openPayment === null) throw new Error('No session')
    return readCorrections(reads, 'payment', openPayment)
  }, [openPayment, reads])

  /** The same two for the open job, which is `COR-001`'s other correctable record here. */
  const loadJobCorrectionOffers = useCallback(async () => {
    if (!reads || openJob === null) throw new Error('No session')
    return readCorrectionOffers(reads, 'job', openJob.id)
  }, [openJob, reads])

  const loadJobCorrections = useCallback(async () => {
    if (!reads || openJob === null) throw new Error('No session')
    return readCorrections(reads, 'job', openJob.id)
  }, [openJob, reads])

  /**
   * `INV-001`, for the frame chooser inside `NewOrder`. Stable for the same reason as the lens
   * loader: `FrameChoice` loads in an effect keyed on this prop.
   *
   * Scoped to the branch being worked at, because that is the shelf the sale would relieve, and
   * what another branch stocks is `BR-12`'s commercial leak.
   */
  const loadFrames = useCallback(
    async (query: string) => {
      if (!reads || !branchId) return []
      return readFramesForSale(reads, branchId, query)
    },
    [reads, branchId],
  )

  /**
   * `RES-001`'s holds, for the customer record's panel. Stable, for `loadQueue`'s reason and with
   * a sharper edge here: `CustomerHolds` re-reads in an effect keyed on this function, so an
   * inline arrow re-read the holds on every render of `App`, which is every queue tick.
   */
  const loadHolds = useCallback(
    async (id: string) => {
      if (!reads) return []
      return readHolds(reads, id)
    },
    [reads],
  )

  /** `PRM-001`: every version of every offer. Stable, for `loadQueue`'s reason. */
  const loadPromotions = useCallback(async () => {
    if (!reads) return []
    return readPromotions(reads)
  }, [reads])

  /** `COM-001`: every version of every scheme. Stable, for `loadQueue`'s reason. */
  const loadCommissionSchemes = useCallback(async () => {
    if (!reads) return []
    return readCommissionSchemes(reads)
  }, [reads])

  /** `ADM-005`: the shop's consents and who used them. Stable, because the panel loads it in an effect. */
  const loadSupportAccess = useCallback(async () => {
    if (!reads) throw new Error('not signed in')
    return readSupportAccess(reads)
  }, [reads])

  /** `MSG-002`: what the shop can say. Stable, because the panel loads it in an effect. */
  const loadMessageTemplates = useCallback(async () => {
    if (!reads) return []
    return readMessageTemplates(reads)
  }, [reads])

  /**
   * `REF-001` to `REF-006`: one customer's eye tests. Stable, because the panel loads in an effect
   * keyed on this prop, and keyed on the open customer so it never reads another person's record.
   */
  const openCustomerId = openCustomer?.id
  const loadRefractions = useCallback(async () => {
    if (!reads || !openCustomerId) return []
    return readRefractions(reads, openCustomerId)
  }, [reads, openCustomerId])

  /** `MSG-005`: the outbox. Stable, for `loadQueue`'s reason. */
  const loadMessages = useCallback(
    async (status?: string) => {
      if (!reads || !branchId) return []
      return readMessages(reads, branchId, status)
    },
    [reads, branchId],
  )

  /** `SUP-001`: what is on order with suppliers. Stable, for `loadQueue`'s reason. */
  const loadPurchaseOrders = useCallback(
    async (status?: string) => {
      if (!reads || !branchId) return []
      return readPurchaseOrders(reads, branchId, status)
    },
    [reads, branchId],
  )

  /** `INV-006`: one order with its receipt lines, which is where a `goodsReceiptLineId` comes from. */
  const loadPurchaseOrder = useCallback(
    async (purchaseOrderId: string) => {
      if (!reads) throw new Error('Not signed in')
      return readPurchaseOrder(reads, purchaseOrderId)
    },
    [reads],
  )

  /** `INV-001`: what is on one shelf. Stable, for `loadQueue`'s reason. */
  const loadStockPositions = useCallback(
    async (locationId: string) => {
      if (!reads) return []
      return readStockPositions(reads, locationId)
    },
    [reads],
  )

  /** `INV-005`: one item's movements, which is how a count or a reversal is explained. */
  const loadStockMovements = useCallback(
    async (stockItemId: string) => {
      if (!reads) return []
      return readStockMovements(reads, stockItemId)
    },
    [reads],
  )

  /** `BR-10`: the shelves a delivery can be booked into. Stable, for the same reason. */
  /** §6.19 `SUP-001`. Stable, because the chooser loads it in an effect keyed on it. */
  const loadSuppliers = useCallback(async () => {
    if (!reads) return []
    return readSuppliers(reads)
  }, [reads])

  const loadStockLocations = useCallback(async () => {
    if (!reads || !branchId) return []
    return readStockLocations(reads, branchId)
  }, [reads, branchId])

  /** `IMP-001`: one shipment, by the id an order or a create answer gave. There is no list read. */
  const loadShipment = useCallback(
    async (shipmentId: string) => {
      if (!reads) throw new Error('No session')
      return readShipment(reads, shipmentId)
    },
    [reads],
  )

  /**
   * `IMP-001`: the orders at this branch, which are the only doors into a shipment and the only
   * candidates to attach to one. Stable, for `loadQueue`'s reason.
   *
   * **Two calls, because the default list leaves out a closed order.** A shipment whose orders have
   * all closed is exactly the one a late duty bill arrives for, and `shipmentId` on an order is the
   * only route back to a shipment, so without the second call that shipment cannot be opened at
   * all. Merged by id, the open ones first, so a row that somehow appears in both keeps one entry.
   *
   * `cancelled` is deliberately not asked for: a cancelled order carries no goods, so nothing of a
   * shipment's cost can land on it and attaching one would be a row with nothing in it.
   */
  const loadShipmentOrders = useCallback(async () => {
    if (!reads || !branchId) return []
    const [open, closed] = await Promise.all([
      readShipmentOrders(reads, branchId),
      readShipmentOrders(reads, branchId, 'closed'),
    ])
    const byId = new Map(open.map((order) => [order.id, order]))
    for (const order of closed) if (!byId.has(order.id)) byId.set(order.id, order)
    return [...byId.values()]
  }, [reads, branchId])

  /**
   * `IMP-003`: one order in full, for the delivery lines a shipment is spread across. By id rather
   * than from the list, because the list leaves out a closed order and a closed order's received
   * lines are still part of the spread.
   */
  const loadShipmentOrder = useCallback(
    async (purchaseOrderId: string) => {
      if (!reads) throw new Error('No session')
      return readShipmentOrder(reads, purchaseOrderId)
    },
    [reads],
  )

  /** `REP-001`: the repairs on the bench. Stable, for `loadQueue`'s reason. */
  const loadRepairs = useCallback(
    async (stage?: string) => {
      if (!reads || !branchId) return []
      return readRepairs(reads, branchId, stage)
    },
    [reads, branchId],
  )

  /** `REP-005`: the loans out past their date, which is the only loan list the server has. */
  const loadLoanFrames = useCallback(async () => {
    if (!reads || !branchId) return []
    return readOverdueLoanFrames(reads, branchId)
  }, [reads, branchId])

  /**
   * One loan by id. Not a convenience: the overdue list filters to overdue, so this is the only way
   * a loan that is still in date is read, including the one the counter has just issued.
   */
  const loadLoanFrame = useCallback(
    async (loanFrameId: string) => {
      if (!reads) throw new Error('No session')
      return readLoanFrame(reads, loanFrameId)
    },
    [reads],
  )

  /** `APT-004`: the recall queue. Stable, for `loadQueue`'s reason. */
  const loadRecalls = useCallback(
    async (status?: string) => {
      if (!reads || !branchId) return []
      return readRecalls(reads, branchId, status)
    },
    [reads, branchId],
  )

  /** `MSG-005a`. Stable, for `loadQueue`'s reason: the screen loads in an effect keyed on it. */
  const loadNotifications = useCallback(
    async (status: NotificationTab) => {
      if (!reads || !branchId) return []
      return readNotifications(reads, branchId, status)
    },
    [reads, branchId],
  )

  /** `RPT-01`: the reports this role may run. Stable, for `loadQueue`'s reason. */
  const loadReportListing = useCallback(async () => {
    if (!reads) throw new Error('No session')
    return readReportListing(reads)
  }, [reads])

  /**
   * Section 15.20 step 4: the panels a statement can be issued for, and one panel's statements.
   *
   * Both throw without a session rather than answering empty, which is `loadReportListing`'s rule
   * above: an empty list reads as "this panel has never been sent anything", and that is the
   * sentence a shopkeeper would act on by issuing a second copy of a statement that already went
   * out. A statement is append-only, so the second copy would be a revision nobody asked for.
   */
  const loadPanels = useCallback(async () => {
    if (!reads) throw new Error('No session')
    return readPanels(reads)
  }, [reads])

  const loadPanelStatements = useCallback(
    async (panelId: string) => {
      if (!reads) throw new Error('No session')
      return readPanelStatements(reads, panelId)
    },
    [reads],
  )

  /**
   * One panel with its plans and what each plan pays. §6.13 `CLM-001` to `CLM-003`.
   *
   * Throws without a session rather than answering an empty panel, which is `loadPanels`' rule
   * above: a plan list that reads as empty is the sentence a shopkeeper would act on by adding a
   * second copy of a plan that is already there, and the name is uniquely indexed, so the write
   * would be refused and sit blocked at the head of the queue.
   */
  const loadPanel = useCallback(
    async (panelId: string) => {
      if (!reads) throw new Error('No session')
      return readPanel(reads, panelId)
    },
    [reads],
  )

  /** What may be claimed on one sale, and the claim if there is one. §15.20 steps 1 to 3. */
  const loadClaimCapture = useCallback(
    async (orderId: string) => {
      if (!reads) throw new Error('No session')
      return readClaimCapture(reads, orderId)
    },
    [reads],
  )

  /**
   * `CLM-004`'s aged claims, in one panel's currency.
   *
   * **The day is the branch's own, and the route has no default for it.** `asOf` arrives at
   * `asIsoDay` as `''` when the query omits it and comes back a 422, so a read without one is a
   * refusal rather than today. The timezone is read out as a string rather than closing over the
   * branch object, so this callback is stable while the shopkeeper stays in one branch and the
   * screen's load effect does not re-fire on every render of the shell.
   */
  const loadAged = useCallback(
    async (currency: string) => {
      if (!reads || branchTimezone === null) throw new Error('No session')
      return readAged(reads, currency, branchDate(Date.now(), branchTimezone))
    },
    [reads, branchTimezone],
  )

  const runReport = useCallback(
    async (reportId: string, parameters: Readonly<Record<string, string>>) => {
      if (!reads) throw new Error('No session')
      return readReport(reads, reportId, parameters)
    },
    [reads],
  )

  /** `PRC-012`: the shop's own rounding rule, never a direction chosen here. */
  const pricingRounding = context?.settings.pricingRoundingMode ?? null
  const loadPriceImpact = useCallback(
    async (versionId: string) => {
      if (!reads) throw new Error('No session')
      if (pricingRounding === null) throw new Error(t('priceImpact.noRounding'))
      return readPriceImpact(reads, versionId, pricingRounding)
    },
    [reads, pricingRounding, t],
  )

  // Stable, because `MiraSurface` re-reads the balance in an effect keyed on it and an inline arrow
  // would re-read on every render of this component.
  const loadMiraQuota = useCallback(async () => {
    if (!reads || !branchId) return []
    return readMiraQuota(reads, branchId)
  }, [reads, branchId])

  /* ---------------------------------------------------------------------- *
   * The loaders for the seven screens of this round.
   *
   * Every one is a `useCallback`, for `loadQueue`'s reason stated above it: each screen reads in an
   * effect keyed on the identity of its loader, so an inline arrow is the fetch, set, render, fetch
   * loop that made the work queue flash its own skeleton back over itself.
   *
   * **A rejected read reaches the screen as a rejection.** None of these catches. Returning an
   * empty list for a read that failed renders "there are none", which has shipped twice in this
   * repository, and three of these screens read `status` or `code` off the rejection: `Diary.tsx`
   * for the denied day and for `APPOINTMENT_SCHEDULE_MISSING`, `SupplierPrices.tsx` for the cost
   * wall. `[]` here means `reads` is null, which is signed out, and the shell renders the sign-in
   * screen in that case rather than any of these.
   * ---------------------------------------------------------------------- */

  /** `BR-12`: the groups `shared_group` resolves through. Shop-wide, so no branch. */
  const loadBranchGroups = useCallback(async () => {
    if (!reads) return []
    return readBranchGroups(reads)
  }, [reads])

  /** `APT-004`: all four kinds, configured or not. */
  const loadRecallRules = useCallback(async () => {
    if (!reads) return []
    return readRecallRules(reads)
  }, [reads])

  /**
   * `APT-002`: the days a branch is shut, and its seasonal hours.
   *
   * Both take the branch as an argument rather than closing over `branchId`, because the screen
   * passes its own `branchId` prop back: one source for which branch is being configured, so a
   * stale closure cannot write a Sunday off against the wrong shop.
   */
  /**
   * `APT-001`: the branch's base weekly hours and slot rules.
   *
   * **Throws rather than answering null with no session**, like `loadVisitDates` and unlike the
   * closures loader. Null is a real answer here, "this branch has no hours", so inventing it for a
   * read that never happened puts the screen in the unset state, and the save that follows omits
   * `expectedVersion` against a row that has one: a refusal at the head of the queue.
   *
   * Takes the branch as an argument rather than closing over `branchId`, so the screen's own prop
   * is the single source of which branch is being configured.
   */
  /**
   * `POS-008a`: the shop's policy numbers, which carry the `version` a save goes back with.
   *
   * Throws with no session rather than answering an empty row. An invented version is a conflict at
   * the head of the write queue, and an invented null is a discount limit silently removed from
   * every role.
   */
  const loadPolicies = useCallback(async () => {
    if (!reads) throw new Error('No session')
    return readTenantPolicies(reads)
  }, [reads])

  const loadAppointmentSchedule = useCallback(
    async (id: string) => {
      if (!reads) throw new Error('No session')
      return readAppointmentSchedule(reads, id)
    },
    [reads],
  )

  const loadAppointmentClosures = useCallback(
    async (id: string) => {
      if (!reads) return []
      return readAppointmentClosures(reads, id)
    },
    [reads],
  )

  const loadAppointmentOverrides = useCallback(
    async (id: string) => {
      if (!reads) return []
      return readAppointmentHoursOverrides(reads, id)
    },
    [reads],
  )

  /** `APT-001`: what the shop offers. Shop-wide, unlike the rooms below. */
  const loadAppointmentTypes = useCallback(async () => {
    if (!reads) return []
    return readAppointmentTypes(reads)
  }, [reads])

  const loadAppointmentResources = useCallback(
    async (id: string) => {
      if (!reads) return []
      return readAppointmentResources(reads, id)
    },
    [reads],
  )

  /**
   * The days a visiting optometrist is in.
   *
   * This one throws rather than answering an empty set, and the difference is not stylistic: the
   * `PUT` replaces the whole set, so an invented empty month followed by one Save deletes a real
   * one. It also carries the resource's `version`, and there is no honest version to invent.
   */
  const loadVisitDates = useCallback(
    async (resourceId: string) => {
      if (!reads) throw new Error('No session')
      return readVisitDates(reads, resourceId)
    },
    [reads],
  )

  /**
   * `SUP-002`: one supplier's price list.
   *
   * **The 403 is translated here and not in `reads.ts`.** `SupplierPrices.tsx` tells a cost wall
   * apart from a failed read by the error's class, because the two need different surfaces: the
   * wall has no retry and a failed read does. `PriceListDenied` is the screen's own class, and
   * `reads.ts` imports no runtime value from a screen, so the loader is where the status becomes
   * the class. Everything else is rethrown untouched, so a real failure still gets its retry.
   */
  const loadSupplierPrices = useCallback(
    async (supplierId: string) => {
      if (!reads) return []
      try {
        return await readSupplierPrices(reads, supplierId)
      } catch (failure) {
        if (failure instanceof ReadFailed && failure.status === 403) {
          throw new PriceListDenied(failure.code)
        }
        throw failure
      }
    },
    [reads],
  )

  /** `CAT-020`: the contact lens catalogue. The screen always asks for stopped lenses too. */
  const loadClProducts = useCallback(
    async (query: { readonly q: string; readonly includeDiscontinued: boolean }) => {
      if (!reads) return { products: [], truncated: false }
      return readClProducts(reads, query)
    },
    [reads],
  )

  /** `APT-001`: one branch's day, and the filter's narrowing is the server's. */
  const loadDiaryDay = useCallback(
    async (onDate: string, resourceId?: string) => {
      if (!reads || !branchId) return []
      return readBranchDay(reads, branchId, onDate, resourceId)
    },
    [reads, branchId],
  )

  /** `decisions/0039`: what this branch books, both halves in one request. */
  const loadBookable = useCallback(async () => {
    if (!reads || !branchId) return { types: [], resources: [] }
    return readBookable(reads, branchId)
  }, [reads, branchId])

  /**
   * The free times. Throws with no session rather than answering an empty list, because an empty
   * slot list is a sentence about the shop's day and "you are not signed in" is not one of them.
   */
  const loadSlots = useCallback(
    async (query: SlotQuery) => {
      if (!reads || !branchId) throw new Error('No session')
      return readAppointmentSlots(reads, branchId, query)
    },
    [reads, branchId],
  )

  if (!session || !queue || !reads) {
    /**
     * Two screens for somebody with no session, and signing up is the one the product had no way
     * to reach. `OPS-201`: a shop could create an account nowhere, so every path in started with a
     * database provisioned by hand.
     *
     * Local state rather than a route, because the counter has no router: `SECTIONS` is the whole
     * navigation model and it only exists inside the shell. A shop sees sign-in first because
     * that is what the returning majority want, and signing up is a button underneath.
     */
    /**
     * **The frame, not the form.** `decisions/0031`: sign-in was a form at the top of an empty
     * white page with no brand anywhere on it, and the two secondary actions underneath rendered
     * as full-width grey blocks, because `.lm-screen` is a stretching flex column and a bare
     * `.lm-button` fills it. The first screen anybody sees in this product read as an unfinished
     * page with two disabled inputs on it.
     *
     * One wrapper here rather than a class on six `<section>` roots across the two screens: both
     * of them, and every branch inside them, want the same frame.
     */
    const auth = (screen: JSX.Element): JSX.Element => (
      <LocaleProvider locale={locale}>
        <div className="lm-auth">
          {/* Not `aria-hidden`, unlike the rail's. On the rail it is repeated decoration beside
              the thing it names; here it is the only statement of which product this is. */}
          <div className="lm-auth__brand">Lensmira</div>
          {screen}
        </div>
      </LocaleProvider>
    )

    if (signingUp) {
      return auth(
        <SignUp baseUrl={API_BASE_URL} doFetch={fetchImpl} onBack={() => setSigningUp(false)} />,
      )
    }
    return auth(
      <>
        <SignIn
          onSignUp={() => setSigningUp(true)}
          baseUrl={API_BASE_URL}
          doFetch={fetchImpl}
          onSignedIn={(signedIn) => {
            /**
             * **The errors go with the session.** A read that failed before anybody was signed in
             * sets a message, and "Sign in again." is the commonest of them: it is what every read
             * says to a stale token. Left standing, it sits in the shell under every screen for the
             * rest of the shift, telling somebody who just signed in to sign in.
             *
             * Found in a browser walkthrough, where it survived a sign-out, a sign-in and six
             * screens. No test had it, because every screen test renders the shell already signed
             * in.
             */
            setContextError(null)
            setScreenError(null)
            setCustomerError(null)
            setSession(signedIn)
          }}
        />
      </>,
    )
  }

  const signOut = () => {
    forgetSession(session.tenantId)
  }

  /**
   * Changing branch, from the one place that offers it.
   *
   * §16.3 wants the **switcher** always visible, so it is the name in the top bar rather than a
   * second control in the page's own toolbar: two places saying which branch, one of them a
   * control, is how somebody comes to read one and act on the other.
   */
  const changeBranch = (branchId: string): void => {
    const next = context?.branches.find((b) => b.id === branchId)
    setBranchId(branchId)
    // The terminal belongs to the branch, so it is re-picked rather than carried: a till from
    // another branch would put `OFF-003`'s invoice block on the wrong one.
    setTerminalId(next?.terminals[0]?.terminalId ?? null)
    setCashSession(null)
    // And so does everything else in hand. An order opened at Saddar left on screen while the
    // branch chip reads Clifton is the exact confusion `BR-08` records a branch on the sale to
    // prevent, and the payment panel below it would take money against the other branch's till.
    setOpenOrder(null)
    setOpenJob(null)
    setPaying(false)
    setScreenError(null)
    setPrescriptionId(null)
    setRxDraft(null)
    setCapturingRx(false)
  }

  const destinations = SECTIONS.filter(
    (entry) =>
      (permissions.has(entry.permission) ||
        (entry.orPermission !== undefined && permissions.has(entry.orPermission))) &&
      (entry.andPermission === undefined || permissions.has(entry.andPermission)),
  ).map(({ id, label, glyph, group }) => ({
    id,
    label: t(label),
    glyph,
    group,
    // Only the queue carries one, and only when it is known. `Shell` hides a zero, which is right:
    // an empty queue is the good outcome and a "0" badge reads as a problem.
    ...(id === 'queue' && queueCount !== null ? { count: queueCount } : {}),
  }))

  /**
   * **A section this person's rail does not offer is never rendered.**
   *
   * The handover between two people on one tablet is `forgetSession`'s job, not this: it puts the
   * section back to the queue, so an accountant's Imports screen cannot follow them out. Deleting
   * this block leaves those cases passing, which is worth saying plainly rather than letting the
   * next reader assume it is load-bearing for them.
   *
   * **What it is for is the default section itself.** `section` starts at `queue`, and the rail is
   * built from `staff.permissions` as the context read returns them rather than from the compiled
   * matrix, so a shop whose rows differ from Appendix B's defaults can hand this app somebody with
   * no operational report row. They would boot to a section their rail does not list, and the
   * first screen they ever saw would be a 403. No exit from a session can prevent that, because
   * none of them knows what the next person's rows will be.
   *
   * **Corrected during the render and not in an effect.** An effect runs after the commit, by
   * which time the screen has mounted and its reads have gone out, which is the thing that must
   * not happen. React re-runs this render with the new state before anything reaches the DOM. It
   * converges because the replacement comes out of `destinations` itself.
   *
   * An empty `destinations` is the context still loading, which is not a signal to move anybody.
   */
  const firstOffered = destinations[0]
  if (firstOffered !== undefined && !destinations.some((entry) => entry.id === section)) {
    setSection(firstOffered.id as SectionId)
  }

  // `AI-022`: what Mira is told the person is looking at. The most specific thing open wins, and the
  // sale in hand counts: a customer carried to New order is still "this customer".
  const currentSection = SECTIONS.find((entry) => entry.id === section)
  const miraContext: MiraContext =
    section === 'orders' && openJob
      ? { kind: 'job', id: openJob.id, number: openJob.jobNumber }
      : section === 'orders' && openOrder
        ? { kind: 'order', id: openOrder.id, number: openOrder.number }
        : customer
          ? { kind: 'customer', id: customer.id, name: customer.name }
          : { kind: 'section', label: currentSection ? t(currentSection.label) : '' }

  return (
    <LocaleProvider locale={locale}>
      <WriteQueueProvider queue={queue}>
        <Shell
          destinations={destinations}
          groups={RAIL_GROUPS.map((group) => ({ id: group.id, label: t(group.label) }))}
          primary={BOTTOM_BAR}
          current={section}
          onNavigate={(id) => {
            /**
             * Leaving a section drops what was being **read**, not the sale being built.
             *
             * This used to clear the customer too, which was right while nothing was carried forward
             * and became wrong the moment the prescription was: capturing an Rx for a customer and
             * walking to New order produced an order carrying one person's eyes with no name on it,
             * which is worse than either half. Customers-to-New-order is the ordinary path through
             * this app, not an accident to guard against.
             *
             * So the sale in hand, the customer and their prescription, is cleared by the three things
             * that actually end it: picking somebody else, changing branch, and confirming the order.
             * Everything below is a screen's own reading position and still goes.
             */
            setOpenOrder(null)
            setOpenPayment(null)
            setOpenJob(null)
            setPaying(false)
            setScreenError(null)
            setSection(id as SectionId)
          }}
          branchName={branch ? `${branch.name} (${branch.code})` : session.tenantId}
          {...(context === null || branch === null
            ? {}
            : {
                branches: context.branches.map((b) => ({ id: b.id, name: b.name })),
                currentBranchId: branch.id,
                onBranchChange: changeBranch,
              })}
          staffName={context?.staff.displayName ?? t('shell.signedIn')}
          cashSessionOpen={drawerKnown ? cashSession?.status === 'open' : undefined}
          density={density}
          onDensityChange={changeDensity}
          language={context?.staff.language ?? ''}
          onLanguageChange={changeLanguage}
          languagePending={languagePending}
          onSignOut={signOut}
          queueFlag={<QueueFlag />}
        >
          {/* Above the screen rather than beside it: a blocked write stops every later write, so it
            is not a footnote to whatever the shop is doing next. */}
          <BlockedWrites />

          {/*
            **The page toolbar: what sits above every screen rather than inside one.**

            Mira and the branch chooser were two siblings with the error paragraphs between them,
            so a screen's own heading arrived third and the page read as a pile of controls with a
            title somewhere in it. One row, with the branch at the start because it changes what
            every number below it means, and Mira at the end because it is asked for rather than
            read.
          */}
          <div className="lm-toolbar">
            {context && branch && permissions.has('report_operational') ? (
              // `AI-022`: on every screen, not a screen. Mounted here so it sees what the shell sees.
              <MiraSurface
                branchId={branch.id}
                context={miraContext}
                loadQuota={loadMiraQuota}
                {...(customer !== null && permissions.has('prescription_create_version')
                  ? {
                      onTakeRx: (grid: GridState) => {
                        setRxDraft((previous) => ({ grid, serial: (previous?.serial ?? 0) + 1 }))
                        setSection('customers')
                        setCapturingRx(true)
                      },
                    }
                  : {})}
              />
            ) : null}
          </div>

          {contextError ? (
            <p className="lm-error" role="alert">
              {contextError}
            </p>
          ) : null}
          {screenError ? (
            <p className="lm-error" role="alert">
              {screenError}
            </p>
          ) : null}

          {context && context.branches.length === 0 ? (
            // `BR-01` fails closed: no `staff_branch_access` row means this member may act nowhere.
            // Said plainly, because the alternative is an empty screen that reads as a broken app.
            <p className="lm-empty">{t('app.noBranch')}</p>
          ) : null}

          {context && branch ? (
            <>
              {section === 'queue' ? (
                // One request serves the screen and the badge, so the two cannot disagree about how
                // much is waiting, and `loadQueue` is stable so asking once does not turn into a loop.
                <>
                  {/* `OPS-204`, above the queue because this is the screen a new shop lands on and
                      the one it finds least useful: an empty shop has nothing waiting. It renders
                      nothing once all six are done. */}
                  <Onboarding key={`onboarding:${branch.id}`} load={loadOnboarding} />
                  {/* `OPS-207`, the owner only: the server refuses everybody else, so the request
                      is not made. One row, never blocking, and absent unless the owner is due. */}
                  {permissions.has('subscription_billing') ? (
                    <Satisfaction load={loadSatisfaction} />
                  ) : null}
                  <WorkQueue key={branch.id} loadQueue={loadQueue} />
                  {/**
                   * §6.14 `APT-001`, and `decisions/0040`: the diary is a panel here rather than a
                   * rail destination, because all twenty-six glyphs are taken and `rail.test.ts`
                   * asserts that as the premise of the decision. This section was already a stack,
                   * so a fourth panel needed no surgery.
                   *
                   * Reachable by everybody who may book: the section is gated on
                   * `report_operational` and booking is `customer_create_edit`, and `rail.test.ts`
                   * asserts the first row covers the second rather than leaving it assumed.
                   *
                   * **The key is load bearing.** Without it an open booking form, a chosen customer
                   * and a chosen time survive a branch switch, and the next Book it sends the new
                   * branch with a slot read from the old one.
                   */}
                  <Diary
                    key={`diary:${branch.id}`}
                    branchId={branch.id}
                    timezone={branch.timezone}
                    // `decisions/0039`: owner, manager, dispenser, cashier, optician. The read side
                    // stays for the other two, because the diary itself is `customer_view`.
                    canBook={permissions.has('customer_create_edit')}
                    // Owner alone. It changes two sentences and no button: only the owner can act
                    // on "no appointment types are set up", so only the owner is told how.
                    canConfigure={permissions.has('tenant_settings_policies')}
                    loadDay={loadDiaryDay}
                    loadBookable={loadBookable}
                    loadSlots={loadSlots}
                    // Appendix K's matcher, through the shell's one customer read path.
                    searchCustomers={findCustomerMatches}
                  />
                </>
              ) : null}

              {section === 'customers' ? (
                customer && capturingRx ? (
                  <RxCapture
                    customerId={customer.id}
                    customerName={customer.name}
                    key={rxDraft?.serial ?? 0}
                    {...(rxDraft === null ? {} : { initialGrid: rxDraft.grid })}
                    onSaved={(saved) => {
                      setRxDraft(null)
                      // Both kept: the next thing a shopkeeper does after capturing a prescription is
                      // sell against it, to the person whose prescription it is.
                      if (saved !== undefined) setPrescriptionId(saved)
                      setCapturingRx(false)
                    }}
                  />
                ) : (
                  <>
                    {openCustomer === null ? null : (
                      <>
                        <button
                          type="button"
                          className="lm-button lm-button--secondary"
                          onClick={() => {
                            setOpenCustomer(null)
                            setCustomerError(null)
                          }}
                        >
                          {t('app.backToSearch')}
                        </button>
                        {/*
                          **Each panel's key names the panel as well as the customer.**

                          All four were `key={openCustomer.id}`, and React wants a key unique
                          among siblings: four of them collided, which React warns is "unsupported"
                          and may duplicate or omit children. The keys are here to remount a panel
                          when the customer changes, so a collision breaks the one thing they were
                          added for. Found as a console warning during a light-mode sweep.
                        */}
                        <CustomerDetail
                          customer={openCustomer}
                          timezone={branch.timezone}
                          {...(permissions.has('prescription_create_version')
                            ? {
                                renderPrescriptionActions: (rx: PrescriptionVersionView) => (
                                  <RxActions
                                    key={rx.id}
                                    rx={rx}
                                    onSettled={() => {
                                      openCustomerById(openCustomer.id, setCustomerError)
                                    }}
                                  />
                                ),
                                renderContactLensActions: (rx: ContactLensPrescriptionView) => (
                                  <ClRxActions
                                    key={rx.id}
                                    rx={rx}
                                    onSettled={() => {
                                      openCustomerById(openCustomer.id, setCustomerError)
                                    }}
                                  />
                                ),
                              }
                            : {})}
                          {...(reads === null || branch === null
                            ? {}
                            : {
                                /**
                                 * `RES-001`. The panel owns its own read and its own writes, so
                                 * this passes the loaders rather than the data: the customer read
                                 * above is one endpoint and a hold is another, and folding holds
                                 * into it would make every customer open cost a second query
                                 * nobody asked for.
                                 */
                                renderHolds: () => (
                                  <CustomerHolds
                                    key={`holds-${openCustomer.id}`}
                                    customerId={openCustomer.id}
                                    load={loadHolds}
                                    loadFrames={loadFrames}
                                    canHold={
                                      permissions.has('stock_view') &&
                                      permissions.has('dispense_create')
                                    }
                                    timezone={branch.timezone}
                                    nowIso={new Date().toISOString()}
                                  />
                                ),
                                onPrescriptionCopy: (prescriptionId: string) => {
                                  setRxCopyError(null)
                                  readPrescriptionCopy(reads, prescriptionId).then(
                                    setOpenRxCopy,
                                    (error: unknown) => {
                                      // A 409 means this prescription has no copy and a 410 means
                                      // the customer was erased; the server explains both. Shown
                                      // rather than swallowed: the alternative is a button that
                                      // does nothing.
                                      setRxCopyError(
                                        error instanceof ReadFailed
                                          ? error.message
                                          : t('rxCopy.failed'),
                                      )
                                    },
                                  )
                                },
                              })}
                        />
                        {rxCopyError === null ? null : (
                          <p className="lm-error" role="alert">
                            {rxCopyError}
                          </p>
                        )}
                        {openRxCopy === null ? null : (
                          <>
                            <button
                              type="button"
                              className="lm-button lm-button--secondary lm-no-print"
                              onClick={() => {
                                setOpenRxCopy(null)
                              }}
                            >
                              {t('rxCopy.close')}
                            </button>
                            <PrescriptionCopy copy={openRxCopy} timezone={branch.timezone} />
                          </>
                        )}
                        {permissions.has('message_send_manual') ? (
                          <SendMessage
                            key={`message:${openCustomer.id}`}
                            customerId={openCustomer.id}
                            branchId={branch.id}
                            loadTemplates={loadMessageTemplates}
                          />
                        ) : null}
                        {permissions.has('prescription_create_version') ? (
                          <button
                            type="button"
                            className="lm-button"
                            onClick={() => {
                              setCapturingRx(true)
                            }}
                          >
                            {t('app.capturePrescription')}
                          </button>
                        ) : null}
                        {branchId && context ? (
                          <Refractions
                            key={`refractions:${openCustomer.id}`}
                            customerId={openCustomer.id}
                            branchId={branchId}
                            staffId={context.staff.id}
                            timezone={branch.timezone}
                            // Appendix B has no row for a sight test; the prescription's pair
                            // stands in (`decisions/0024`), and the server checks the same two.
                            canRecord={permissions.has('prescription_create_version')}
                            canView={permissions.has('prescription_view')}
                            loadRefractions={loadRefractions}
                          />
                        ) : null}
                        <Fittings
                          key={`fittings:${openCustomer.id}`}
                          customerId={openCustomer.id}
                          // `DSP-002` takes these at dispense, which is Appendix B's
                          // `dispense_create`, the same row the lens chooser takes.
                          canMeasure={permissions.has('dispense_create')}
                          loadFrames={loadFrames}
                          onRecorded={() => {
                            // Re-read: the record's own fittings section is what a dispenser checks
                            // on the next visit, and it is on this screen.
                            openCustomerById(openCustomer.id, setCustomerError)
                          }}
                        />
                        <CustomerExport
                          key={`export:${openCustomer.id}`}
                          customerId={openCustomer.id}
                          customerName={openCustomer.name}
                          // Appendix B's `Customer - export`. The filtering inside the file is
                          // `CUS-006a`'s and is the server's: an accountant holds this row and is
                          // denied the prescriptions, so the screen must open for them.
                          canExport={permissions.has('customer_export')}
                          offerDownload={offerDownload}
                        />
                        <CustomerPortalLink
                          key={`portal:${openCustomer.id}`}
                          customerId={openCustomer.id}
                          customerName={openCustomer.name}
                          // Appendix B's `Prescription - print, share`, the row the server checks. What
                          // the link then shows is the issuer's own `prescription_view`, decided there.
                          canShare={permissions.has('prescription_print_share')}
                          timezone={branch.timezone}
                          origin={window.location.origin}
                          copyText={(text) => navigator.clipboard.writeText(text)}
                        />
                        <CustomerErasure
                          key={`erasure:${openCustomer.id}`}
                          customerId={openCustomer.id}
                          customerName={openCustomer.name}
                          loadPlan={loadErasurePlan}
                          // Appendix B's `Customer - erase`, the row `POST /customers/:id/erasure`
                          // itself takes. The panel renders nothing without it rather than offering
                          // a door that answers 403.
                          canErase={permissions.has('customer_erase')}
                          timezone={branch.timezone}
                          onErased={() => {
                            // Re-read: the record is still there and almost everything on it has
                            // changed, and the screen must not keep showing a name that no longer
                            // exists in the database.
                            openCustomerById(openCustomer.id, setCustomerError)
                          }}
                        />
                      </>
                    )}
                    {customerError === null ? null : <p className="lm-error">{customerError}</p>}
                    {openCustomer === null ? (
                      <CustomerSearch
                        baseUrl={API_BASE_URL}
                        token={session.token}
                        doFetch={fetchImpl}
                        onPick={(picked) => {
                          // A prescription belongs to one person. Choosing someone else drops it
                          // rather than carrying it onto their order.
                          setPrescriptionId(null)
                          setRxDraft(null)
                          setCustomer(picked)
                          setCustomerError(null)
                          // The record this clears is the previous customer's, so the panel
                          // cannot show one person while the Rx screen holds another's id even
                          // for the length of a read.
                          setOpenCustomer(null)
                          openCustomerById(picked.id, setCustomerError)
                        }}
                      />
                    ) : null}
                    {openCustomer === null && permissions.has('customer_create_edit') ? (
                      // Appendix B's `Customer - create/edit`. Gated on the row the route checks,
                      // so there is no door that answers 403: `POST /customers` takes the same one.
                      filingCustomer ? (
                        <NewCustomer
                          branchId={branch.id}
                          findMatches={findCustomerMatches}
                          onOpen={(customerId) => {
                            // The new record, or an existing one the form offered instead. Either
                            // way the shop wanted this person open, which is what the next screen
                            // needs: a prescription belongs to a customer id.
                            setFilingCustomer(false)
                            setPrescriptionId(null)
                            setRxDraft(null)
                            setCustomer(null)
                            setOpenCustomer(null)
                            setCustomerError(null)
                            openCustomerById(customerId, setCustomerError)
                          }}
                        />
                      ) : (
                        <button
                          type="button"
                          className="lm-button"
                          onClick={() => {
                            setFilingCustomer(true)
                          }}
                        >
                          {t('app.newCustomer')}
                        </button>
                      )
                    ) : null}
                  </>
                )
              ) : null}

              {section === 'new-order' ? (
                branch.invoiceSeriesTemplate === null ||
                terminalId === null ||
                context.settings.pricingRoundingMode === null ? (
                  // `OFF-003` numbers come from a pre-allocated per-terminal block and `TAX-002` from
                  // a series. Neither is derivable here, and inventing a format would put a made-up
                  // number on a tax invoice. So the screen refuses and names what is missing.
                  <p className="lm-empty">
                    {[
                      t('app.cannotOrder'),
                      ...(terminalId === null ? [t('app.cannotOrder.noTerminal')] : []),
                      ...(branch.invoiceSeriesTemplate === null
                        ? [t('app.cannotOrder.noSeries')]
                        : []),
                      ...(context.settings.pricingRoundingMode === null
                        ? [t('app.cannotOrder.noRounding')]
                        : []),
                    ].join(' ')}
                  </p>
                ) : (
                  <NewOrder
                    branchId={branch.id}
                    terminalId={terminalId}
                    currency={branch.currency}
                    // `PRC-013`: the shop-s own rounding rule, read from its settings. The branch
                    // above refuses when it is null, so this is never a direction chosen here.
                    rounding={context.settings.pricingRoundingMode}
                    fiscalYear={branch.terminals[0]?.fiscalYear ?? new Date().getFullYear()}
                    seriesCode={branch.terminals[0]?.seriesCode ?? 'INV'}
                    seriesTemplate={branch.invoiceSeriesTemplate}
                    {...(customer ? { customerId: customer.id, customerName: customer.name } : {})}
                    loadFrames={loadFrames}
                    {...(prescriptionId === null ? {} : { prescriptionId, loadLensSelection })}
                    onCreated={refreshOrders}
                    onConfirmed={() => {
                      // The sale is finished, so the customer and their prescription stop being in
                      // hand. Not at create: the confirm step is still to come and needs them.
                      refreshOrders()
                      setCustomer(null)
                      setPrescriptionId(null)
                    }}
                  />
                )
              ) : null}

              {section === 'dispensing' ? (
                <Dispensing
                  key={branch.id}
                  branchId={branch.id}
                  timezone={branch.timezone}
                  loadOpen={loadOpenDispenses}
                  loadOne={loadDispenseById}
                  {...(customer ? { customerId: customer.id, customerName: customer.name } : {})}
                  {...(prescriptionId === null ? {} : { prescriptionId })}
                />
              ) : null}

              {section === 'orders' ? (
                openReceipt ? (
                  <>
                    <button
                      type="button"
                      className="lm-button lm-button--secondary lm-no-print"
                      onClick={() => {
                        setOpenReceipt(null)
                      }}
                    >
                      {t('app.backToOrders')}
                    </button>
                    <Receipt receipt={openReceipt} timezone={branch.timezone} />
                  </>
                ) : openQuote ? (
                  <>
                    <button
                      type="button"
                      className="lm-button lm-button--secondary"
                      onClick={() => {
                        setOpenQuote(null)
                      }}
                    >
                      {t('app.backToOrders')}
                    </button>
                    <Quote
                      quote={openQuote}
                      timezone={branch.timezone}
                      {...(customer ? { customerName: customer.name } : {})}
                      onSettled={() => {
                        // Re-read rather than patching the status in place: a conversion creates an
                        // order and `checkQuoteConversion` is the server's verdict, so a screen that
                        // guessed would offer a step the write path refuses.
                        readQuote(reads, openQuote.id)
                          .then(setOpenQuote)
                          .catch(() => undefined)
                        refreshOrders()
                      }}
                      onOpenOrder={(orderId) => {
                        setOpenQuote(null)
                        setScreenError(null)
                        readOrder(reads, orderId)
                          .then(setOpenOrder)
                          .catch((error: unknown) => {
                            setScreenError(
                              error instanceof ReadFailed
                                ? error.message
                                : t('app.orderOpenFailed'),
                            )
                          })
                      }}
                    />
                  </>
                ) : openJob ? (
                  <>
                    {/* `lm-no-print`: this button is a direct child of `main.lm-content`, not of a
                        `.lm-screen`, so the print block's `.lm-screen > *` rule does not reach it
                        and it would print above the job ticket (`JOB-011`). */}
                    <button
                      type="button"
                      className="lm-button lm-button--secondary lm-no-print"
                      onClick={() => {
                        setOpenJob(null)
                      }}
                    >
                      {t('app.back')}
                    </button>
                    <JobDetail
                      job={openJob}
                      branchId={branch.id}
                      terminalId={terminalId ?? ''}
                      // `remake_raise`, which Appendix B keeps separate from the transition row:
                      // `RMK-001` makes raising a remake a decision about who bears a cost.
                      canRaiseRemake={permissions.has('remake_raise')}
                      // `RMK-008`: the lab credit and the close, which no route wrote until this
                      // release, and which the work queue already offered a `Resolve` action for,
                      // pointing at a route that did not exist.
                      //
                      // `remake_set_cost_attribution` is the row the server gates all three on.
                      // `timezone` is the branch's and never the device's (`LOC-004`): a date
                      // pushed through the wrong zone moves a day west of UTC.
                      {...(openJob.remakes === undefined
                        ? {}
                        : {
                            remakeLife: {
                              rows: openJob.remakes,
                              canAct: permissions.has('remake_set_cost_attribution'),
                              timezone: branch.timezone,
                            },
                          })}
                      {...(cashSession?.status === 'open' ? { cashSessionId: cashSession.id } : {})}
                      onActionQueued={() => {
                        // Re-read rather than patching the job in place. `JOB-005a`'s gate and the
                        // permitted transitions are the server's answer, and a screen that guessed
                        // the next state would offer a step the write path then refuses.
                        readJob(reads, openJob.id)
                          .then(setOpenJob)
                          .catch(() => undefined)
                        refreshOrders()
                      }}
                    />
                    {/* `JOB-011`: the only way a job reaches a lab in v1, since `V1-SPEC.md` §2
                        excludes lab EDI. `job_send_to_lab` is the Appendix B row the route checks,
                        so the button is not offered where it would be a 403. */}
                    {permissions.has('job_send_to_lab') ? (
                      openTicket === null ? (
                        <button
                          type="button"
                          className="lm-button lm-button--secondary lm-no-print"
                          onClick={() => {
                            setTicketError(null)
                            if (!reads) return
                            readJobTicket(reads, openJob.id).then(
                              setOpenTicket,
                              (error: unknown) => {
                                // A 409 means this job cannot produce a ticket, which the server
                                // explains. Shown rather than swallowed: the alternative is a button
                                // that does nothing.
                                setTicketError(
                                  error instanceof ReadFailed
                                    ? error.message
                                    : t('jobTicket.failed'),
                                )
                              },
                            )
                          }}
                        >
                          {t('jobTicket.open')}
                        </button>
                      ) : (
                        <>
                          <button
                            type="button"
                            className="lm-button lm-button--secondary lm-no-print"
                            onClick={() => {
                              setOpenTicket(null)
                            }}
                          >
                            {t('jobTicket.close')}
                          </button>
                          <JobTicket ticket={openTicket} />
                        </>
                      )
                    ) : null}
                    {ticketError === null ? null : (
                      <p className="lm-error" role="alert">
                        {ticketError}
                      </p>
                    )}

                    {permissions.has('correction_reassign') ? (
                      // `COR-001`'s "both sides show the link" is about the record, and a job is
                      // one of the four. `job:wrong_branch` is also the only one of the four
                      // supported combinations that moves a job, so without this panel it was
                      // reachable from the API and from nowhere a shopkeeper can get to.
                      <Corrections
                        key={`correct:${openJob.id}`}
                        recordType="job"
                        recordId={openJob.id}
                        // `TAX-002a`: short, speakable, and the thing a customer reads out.
                        recordLabel={openJob.jobNumber}
                        canCorrect
                        loadOffers={loadJobCorrectionOffers}
                        loadMade={loadJobCorrections}
                        searchCustomers={findCustomerMatches}
                        loadStaff={loadStaff}
                        branches={context.branches}
                        branchTimezone={branch.timezone}
                        onCorrected={() => {
                          readJob(reads, openJob.id)
                            .then(setOpenJob)
                            .catch(() => undefined)
                          refreshOrders()
                        }}
                      />
                    ) : null}
                  </>
                ) : openOrder ? (
                  <>
                    <button
                      type="button"
                      className="lm-button lm-button--secondary"
                      onClick={() => {
                        setOpenOrder(null)
                        setPaying(false)
                        setReturning(false)
                      }}
                    >
                      {t('app.backToOrders')}
                    </button>
                    <OrderDetail
                      order={openOrder}
                      branchTimezone={branch.timezone}
                      {...(permissions.has('correction_reassign')
                        ? { onCorrectPayment: setOpenPayment }
                        : {})}
                    />
                    {/*
                      **Membership, not a cleared flag.** `openOrder` is set from five places and
                      only two of them clear a reading position; opening a second order while a
                      payment of the first was picked would have left this panel pointing at that
                      payment, beside the wrong order, with the new order's number in its
                      sentences. Asking whether the payment is on the order being shown makes that
                      state unreachable rather than something five call sites have to remember.
                    */}
                    {openPayment !== null &&
                    openOrder.payments.some((payment) => payment.id === openPayment) &&
                    permissions.has('correction_reassign') ? (
                      /**
                       * `COR-001`'s fourth combination, beside the order rather than inside it.
                       *
                       * `OrderDetail` says in its own header that it is a read and makes no
                       * writes, and that claim is worth keeping: this panel is a sibling of it,
                       * exactly as the sale's and the job's panels are. The screen picked the
                       * payment; the panel takes the write.
                       */
                      <Corrections
                        key={`correct-payment:${openPayment}`}
                        recordType="payment"
                        recordId={openPayment}
                        /**
                         * A payment has no number of its own. `TAX-002a` numbers invoices and
                         * jobs, and `later_todo.md` 52 is where a repair's missing series is
                         * recorded; naming the sale is what the panel's sentences need, and it is
                         * what a shopkeeper would say out loud.
                         */
                        recordLabel={openOrder.number}
                        canCorrect
                        loadOffers={loadPaymentCorrectionOffers}
                        loadMade={loadPaymentCorrections}
                        searchCustomers={findCustomerMatches}
                        loadStaff={loadStaff}
                        branches={context.branches}
                        branchTimezone={branch.timezone}
                        onCorrected={() => {
                          // The order carries the payment rows, so the whole record is re-read.
                          readOrder(reads, openOrder.id)
                            .then(setOpenOrder)
                            .catch(() => undefined)
                          setOpenPayment(null)
                        }}
                      />
                    ) : null}
                    {permissions.has('discount_apply_within_limit') ? (
                      // Appendix B's "Discount - apply within limit", which is the row the apply
                      // endpoint itself takes: taking an offer is taking a discount at the till.
                      <ApplyOffers
                        // Keyed on the order alone. Keyed on the version as well, applying an
                        // offer bumped it, so the panel remounted and threw away the applied and
                        // suppressed lists that are the whole point of section 15.16.
                        //
                        // Prefixed, because these panels are siblings and four of them keyed on
                        // the same order id is React's "two children with the same key", which it
                        // warns may duplicate or omit a child. Found in the browser console.
                        key={`offers:${openOrder.id}`}
                        orderId={openOrder.id}
                        orderVersion={openOrder.version}
                        status={openOrder.status}
                        rounding={context.settings.pricingRoundingMode}
                        onApplied={() => {
                          if (reads === null) return
                          readOrder(reads, openOrder.id)
                            .then(setOpenOrder)
                            .catch((error: unknown) => {
                              setScreenError(
                                error instanceof ReadFailed
                                  ? error.message
                                  : t('app.orderOpenFailed'),
                              )
                            })
                        }}
                      />
                    ) : null}
                    {/**
                     * §6.13 `CLM-002`, `CLM-003` and §15.20 steps 2 and 3: what the panel pays of
                     * this sale.
                     *
                     * **Either row, because the route takes either.**
                     * `GET /orders/:orderId/claim` is `requireAnyPermission(['claim_capture',
                     * 'panel_remittance_record'])`: a dispenser splits the claim while serving the
                     * customer and an accountant settles or writes the same claim off afterwards,
                     * and both read this panel. Requiring both would hide it from the dispenser who
                     * captures; requiring only the first would hide it from the accountant who is
                     * allowed to act. The screen then offers each action against the row that
                     * action needs, which is why both are passed separately below.
                     *
                     * **Nothing here tenders money.** §15.20 step 3 is explicit that a panel's
                     * share is a receivable and not a payment, so capturing records what the payer
                     * owes and takes nothing: the order's status, total and version are unchanged
                     * and no payment row is written. `claim-capture.integration.test.ts` asserts
                     * that, because it is the half a cashier is most likely to read as done.
                     */}
                    {permissions.has('claim_capture') ||
                    permissions.has('panel_remittance_record') ? (
                      <ClaimCapture
                        // Prefixed and keyed on the order alone, for the reason `ApplyOffers`
                        // above gives: four sibling panels keyed on the same id is React's "two
                        // children with the same key".
                        key={`claim:${openOrder.id}`}
                        orderId={openOrder.id}
                        load={loadClaimCapture}
                        canCapture={permissions.has('claim_capture')}
                        canSettle={permissions.has('panel_remittance_record')}
                        timezone={branch.timezone}
                        onChanged={() => {
                          // A claim does not change the order today, and re-reading it is still
                          // right: `../later_todo.md` records that `order-detail.service.ts` never
                          // mentions claims, so the bill a customer is shown ignores the panel's
                          // share. When that is fixed this panel is already asking for the figure.
                          if (reads === null) return
                          readOrder(reads, openOrder.id)
                            .then(setOpenOrder)
                            .catch(() => undefined)
                        }}
                      />
                    ) : null}
                    {permissions.has('job_create_advance_status') && openOrderRx !== 'loading' ? (
                      // Appendix B's "Job - create and advance status": owner, manager, dispenser
                      // and optician, which is the row `POST /jobs` itself checks. The person who
                      // sells the glasses is the person who sends them to the lab.
                      <SendToLab
                        // Keyed on the order alone, like the panels beside it: keyed on the
                        // version, raising a job would remount the form and throw away the
                        // figures somebody had just checked. Prefixed for the reason above.
                        key={`lab:${openOrder.id}`}
                        orderId={openOrder.id}
                        // Nullable, because both panels print it in a sentence and pick their
                        // own wording when there is nothing to name.
                        orderNumber={openOrder.number}
                        status={openOrder.status}
                        branchId={branch.id}
                        // Rendered only once the prescription read has settled, which is why
                        // the state has a third value. The panel fills its boxes from `rx` in a
                        // state initialiser, so one that mounts before the read returns keeps
                        // empty boxes for ever while telling the dispenser the figures are the
                        // prescription: found by clicking through it against a real server, and
                        // invisible to every component test, because each passes `rx` at mount.
                        {...(openOrderRx === null ? {} : { rx: openOrderRx })}
                        {...(openOrder.prescriptionId === null
                          ? {}
                          : { prescriptionId: openOrder.prescriptionId })}
                        {...(openOrder.saleType === undefined
                          ? {}
                          : { saleType: openOrder.saleType })}
                        jobs={openOrder.jobs}
                        onRaised={() => {
                          // Re-read so the panel's "already at the lab" line names the new job.
                          readOrder(reads, openOrder.id)
                            .then(setOpenOrder)
                            .catch(() => undefined)
                          void loadQueue()
                        }}
                      />
                    ) : null}
                    {permissions.has('correction_reassign') ? (
                      // Appendix B's `Correction - reassign`, marked sensitive, which is the row
                      // all three correction routes take. `COR-001`'s "both sides show the link"
                      // is why the panel sits on the record rather than on a report: a sale moved
                      // to another customer looks, everywhere else, like a sale that always was.
                      <Corrections
                        // Keyed on the order alone, like the panels beside it.
                        //
                        // It was keyed on the version too, so any other panel's write remounted
                        // this one and threw away a half-filled form. The version it sends does
                        // not come from here: `loadCorrectionOffers` depends on `openOrder`, so a
                        // re-read changes the loader and the panel fetches the fresh version with
                        // it. That is also why the panel refuses to send while the queue is
                        // holding a write of this till's own.
                        key={`correct:${openOrder.id}`}
                        recordType="sale"
                        recordId={openOrder.id}
                        // The wire answers null now, so there is no literal to compare against.
                        recordLabel={openOrder.number}
                        canCorrect
                        loadOffers={loadCorrectionOffers}
                        loadMade={loadCorrections}
                        searchCustomers={findCustomerMatches}
                        loadStaff={loadStaff}
                        // `BR-01`: the branches this session holds, which is the same check the
                        // server makes on both ends of a `wrong_branch` move.
                        branches={context.branches}
                        branchTimezone={branch.timezone}
                        onCorrected={() => {
                          // Re-read rather than patch: the correction writes the order, the
                          // correction record and an audit row in one transaction.
                          readOrder(reads, openOrder.id)
                            .then(setOpenOrder)
                            .catch(() => undefined)
                          refreshOrders()
                        }}
                      />
                    ) : null}
                    {permissions.has('sale_void') ? (
                      // Appendix B's "Sale, void": owner and manager, marked sensitive. It is the
                      // row `POST /orders/void` itself takes, and `payment_refund` is a different
                      // one: a cashier who may refund a collected sale may not void an uncollected
                      // one, and the reverse holds too. No door that answers 403.
                      <VoidSale
                        // Keyed on the order alone. Keyed on the version as well, the void would
                        // bump it and the panel would remount, throwing away the returned and
                        // retained figures that are `OPS-105`'s refund decision. Prefixed for the
                        // reason on the offers panel above.
                        key={`void:${openOrder.id}`}
                        orderId={openOrder.id}
                        orderNumber={openOrder.number}
                        status={openOrder.status}
                        currency={openOrder.currency}
                        // `BR-01`: the order's own branch, which the server checks against this
                        // caller's. `BR-08`: the compensating payment rows carry the attribution.
                        branchId={branch.id}
                        terminalId={terminalId ?? ''}
                        {...(cashSession?.status === 'open'
                          ? { cashSessionId: cashSession.id }
                          : {})}
                        payments={openOrder.payments}
                        // `POS-007`. Null means the shop has configured no policy, which is not
                        // zero: `proposeForfeit` refuses rather than defaulting, so the panel does
                        // not offer an option the server would refuse.
                        forfeitPercent={context.settings.layawayForfeitPercent}
                        onVoided={() => {
                          // Re-read rather than patch the status on screen: the void writes the
                          // order, the payments and the stock movements in one transaction.
                          readOrder(reads, openOrder.id)
                            .then(setOpenOrder)
                            .catch(() => undefined)
                          refreshOrders()
                        }}
                      />
                    ) : null}
                    <button
                      type="button"
                      className="lm-button lm-button--secondary"
                      onClick={() => {
                        setScreenError(null)
                        // The server is the gate: a draft has no invoice number and a voided sale
                        // has a credit note instead, and both come back as a 409 whose message
                        // names which. Showing that sentence beats a button this screen hides on a
                        // rule it would have to keep in step with `buildSaleReceipt`.
                        readReceipt(reads, openOrder.id)
                          .then(setOpenReceipt)
                          .catch((error: unknown) => {
                            setScreenError(
                              error instanceof ReadFailed
                                ? error.message
                                : t('app.receiptOpenFailed'),
                            )
                          })
                      }}
                    >
                      {t('app.printReceipt')}
                    </button>
                    {paying ? (
                      <TakePayment
                        orderId={openOrder.id}
                        branchId={branch.id}
                        terminalId={terminalId ?? ''}
                        {...(cashSession?.status === 'open'
                          ? { cashSessionId: cashSession.id }
                          : {})}
                        amountDueMinor={openOrder.outstandingMinor}
                        currency={openOrder.currency}
                        onSaved={() => {
                          setPaying(false)
                          // Re-read the order so the outstanding figure on screen is the server-s,
                          // not this screen-s arithmetic. `MR-06` rounding and `MR-08` split tender
                          // both mean the client cannot compute what is left owing.
                          readOrder(reads, openOrder.id)
                            .then(setOpenOrder)
                            .catch(() => undefined)
                          refreshOrders()
                        }}
                      />
                    ) : permissions.has('payment_take') ? (
                      // Appendix B: an accountant or read_only account may open an order but not take
                      // payment, so the button is absent rather than a door that answers 403.
                      <button
                        type="button"
                        className="lm-button"
                        onClick={() => {
                          setPaying(true)
                        }}
                      >
                        {t('app.takePayment')}
                      </button>
                    ) : null}
                    {returning ? (
                      <ReturnLine
                        orderId={openOrder.id}
                        branchId={branch.id}
                        terminalId={terminalId ?? ''}
                        {...(cashSession?.status === 'open'
                          ? { cashSessionId: cashSession.id }
                          : {})}
                        baseUrl={API_BASE_URL}
                        token={session.token}
                        doFetch={fetchImpl}
                        onDone={() => {
                          setReturning(false)
                          // Re-read rather than adjust on screen. A return moves the outstanding
                          // figure and the refunded figure, and `MR-06` rounding means the client
                          // cannot compute either: `requireSomethingToCollect` on the server is the
                          // only thing that knows what is left to collect.
                          readOrder(reads, openOrder.id)
                            .then(setOpenOrder)
                            .catch(() => undefined)
                          refreshOrders()
                        }}
                      />
                    ) : permissions.has('payment_refund') ? (
                      // Appendix B again: `POST /orders/returns` requires `payment_refund`, which a
                      // cashier holds and an optician does not, so the button is absent for anyone
                      // the route would refuse rather than a door that answers 403.
                      <button
                        type="button"
                        className="lm-button lm-button--secondary"
                        onClick={() => {
                          setReturning(true)
                        }}
                      >
                        {t('app.returnLine')}
                      </button>
                    ) : null}
                  </>
                ) : (
                  <OrderList
                    timezone={branch.timezone}
                    key={ordersEpoch}
                    loadOrders={async () => {
                      if (!branchId) return []
                      // The branch name comes from here because the row does not carry it: the list is
                      // scoped to one branch by a required parameter, so it is what was asked for.
                      return readOrders(reads, branchId, branch.timezone, branch.name)
                    }}
                    {...(permissions.has('prescription_view')
                      ? {
                          // Appendix B denies the accountant `Prescription · view`, and a job detail
                          // is the frozen Rx (`JOB-002`), so `GET /jobs/:jobId` refuses them. The
                          // prop is omitted rather than passed, because `OrderList` renders the Open
                          // button only when it has a handler: no door is better than a door that
                          // answers 403. `exactOptionalPropertyTypes` refuses an explicit
                          // `undefined`, hence the spread.
                          onOpenJob: (jobId: string) => {
                            setScreenError(null)
                            readJob(reads, jobId)
                              .then(setOpenJob)
                              .catch((error: unknown) => {
                                setScreenError(
                                  error instanceof ReadFailed
                                    ? error.message
                                    : t('app.jobOpenFailed'),
                                )
                              })
                          },
                        }
                      : {})}
                    onOpen={(orderId) => {
                      setScreenError(null)
                      readOrder(reads, orderId)
                        .then(setOpenOrder)
                        .catch((error: unknown) => {
                          setScreenError(
                            error instanceof ReadFailed ? error.message : t('app.orderOpenFailed'),
                          )
                        })
                    }}
                    onOpenQuote={(orderId) => {
                      setScreenError(null)
                      // The quote endpoint, not the order one: `QuoteView` carries the expiry, the
                      // server's `lapsed` verdict and the price explanation per line, and none of
                      // those are on an order detail.
                      readQuote(reads, orderId)
                        .then(setOpenQuote)
                        .catch((error: unknown) => {
                          setScreenError(
                            error instanceof ReadFailed ? error.message : t('app.quoteOpenFailed'),
                          )
                        })
                    }}
                  />
                )
              ) : null}

              {section === 'stock' ? (
                <Stock
                  // Re-keyed on the epoch as well as the branch, so a receipt or a correction
                  // re-reads the shelf rather than leaving the figure that was on screen before
                  // the write. The shelf is the number somebody counts against.
                  key={`${branch.id}-${String(stockEpoch)}`}
                  loadLocations={loadStockLocations}
                  loadPositions={loadStockPositions}
                  loadMovements={loadStockMovements}
                  // §12 case 118, `REF-010`, `INV-009`: the trial expiry watch. The panel offers
                  // itself only where the branch has a trial case.
                  loadTrialExpiry={(withinDays) => readTrialExpiry(reads, branch.id, withinDays)}
                  timezone={branch.timezone}
                  // Two rows, passed separately. `stock_receive` is owner, manager and dispenser;
                  // `stock_adjust` is owner and manager and Appendix B marks it sensitive, which
                  // is what keeps putting a delivery away and writing off a breakage two separate
                  // decisions by two people.
                  canReceive={permissions.has('stock_receive')}
                  canAdjust={permissions.has('stock_adjust')}
                  onWritten={() => {
                    setStockEpoch((previous) => previous + 1)
                  }}
                />
              ) : null}

              {section === 'promotions' ? (
                <Promotions
                  timezone={branch.timezone}
                  key={branch.id}
                  load={loadPromotions}
                  branchId={branch.id}
                  currency={branch.currency}
                  // The branch's own today, which is what the server compares a window against.
                  today={branchDate(Date.now(), branch.timezone)}
                />
              ) : null}

              {section === 'templates' ? <MessageTemplates load={loadMessageTemplates} /> : null}

              {section === 'transfers' ? (
                <Transfers
                  // Re-keyed on the stock epoch as well as the branch, so a dispatch or a receipt
                  // re-reads both the road and the shelf: the two are one number seen twice and a
                  // stale one invites sending stock that has already gone.
                  key={`${branch.id}-${String(stockEpoch)}`}
                  branchId={branch.id}
                  branches={context.branches}
                  loadTransfers={loadTransfers}
                  loadLocations={loadStockLocations}
                  loadPositions={loadStockPositions}
                  // `stock_transfer_out_in`, the row both writes take. Appendix B gives it to the
                  // owner, the manager and the dispenser, and `stock_view` opens the screen for
                  // everybody, so a cashier sees the road and cannot send anything down it.
                  canMove={permissions.has('stock_transfer_out_in')}
                  timezone={branch.timezone}
                  onWritten={() => {
                    setStockEpoch((previous) => previous + 1)
                  }}
                />
              ) : null}

              {section === 'setup' ? (
                <>
                  <ShopSetup
                    // `TEN-006`: the PIN door is offered only on a deployment that has it on.
                    pinLength={context.pinLength ?? null}
                    role={context.staff.role}
                    // The branches this person may grant, which `BR-01` limits to their own.
                    branches={context.branches}
                    costVisibleBranchIds={context.staff.costVisibleBranchIds ?? []}
                    canOpenBranch={permissions.has('branch_create_close')}
                    // `OFF-003`: the block size is a shop policy, not a till operator's call.
                    canSetPolicies={permissions.has('tenant_settings_policies')}
                    // `TEN-005`, `decisions/0014`: the shop's current answer, so the tick box shows
                    // a state instead of starting blank and reading as off.
                    requireTotp={context.settings.requireTotp === true}
                    // `decisions/0014`: anybody may set up their own, which is what the shop asked
                    // for. Absent from an older server, and then the panel is not offered.
                    {...(context.staff.loginIdentifier === undefined
                      ? {}
                      : { ownLoginIdentifier: context.staff.loginIdentifier })}
                    ownTotpEnrolled={context.staff.totpEnrolled === true}
                    enrolOwnTotp={enrolOwnTotp}
                    branchId={branch.id}
                    // `BR-04`, `BR-10`, `REF-010`: the stock locations panel. Read through the same
                    // mapper the purchasing screen uses, never a cast.
                    loadLocations={() => readStockLocations(reads, branch.id)}
                    // Appendix B's `Staff - offboard`, owner only, which is narrower than hiring.
                    canOffboard={permissions.has('staff_offboard')}
                    // The same row as hiring, and the server takes it for changing somebody and for
                    // resetting a password too: Appendix B has no separate row, because the act is
                    // the same one.
                    canManagePeople={permissions.has('staff_create_assign_role')}
                    staffId={context.staff.id}
                    branchTimezone={branch.timezone}
                    branchCountryCode={branch.countryCode}
                    branchCurrency={branch.currency}
                    loadStaff={loadStaff}
                    onWritten={() => {
                      // A new branch belongs in the branch chooser and a new person in nothing on
                      // screen, so the session context is re-read rather than patched.
                      void refreshContext()
                    }}
                  />
                  {/**
                   * §17 `BR-02` and `BR-12`. Owner only, `tenant_settings_policies`, which is the
                   * row all three of its routes take.
                   *
                   * A panel here rather than inside `ShopSetup`: it renders its own section with
                   * its own heading, and the setup destination is already a stack of them.
                   *
                   * `shared_group` could be chosen and never satisfied before this, because
                   * nothing could make a group, and the fail-closed branch of that join makes
                   * every customer invisible to everybody while the setting says the opposite.
                   */}
                  {permissions.has('tenant_settings_policies') ? (
                    <CustomerSharing
                      canManage={permissions.has('tenant_settings_policies')}
                      // `context.branches`: this owner's own open branches, which is all the editor
                      // can offer, and why a group holding a branch they cannot see is locked.
                      branches={context.branches}
                      customerVisibility={context.settings.customerVisibility}
                      loadGroups={loadBranchGroups}
                      onWritten={() => {
                        // The setting lives in the session context, so it is re-read rather than
                        // patched: the panel's own sentence about what is live comes from there.
                        void refreshContext()
                      }}
                    />
                  ) : null}
                  {/**
                   * §6.14 `APT-001`, Appendix J: the branch's base weekly appointment hours and
                   * slot rules. Owner only, `tenant_settings_policies`, the row both of its routes
                   * take (`decisions/0034`, `OPEN.md` §4.2).
                   *
                   * First of the three appointment panels because the other two are exceptions to
                   * it: `isWorkingDay` reads this row before any closure or season, and without it
                   * `loadBranchSchedule` refuses every booking and every slot read with
                   * `APPOINTMENT_SCHEDULE_MISSING`.
                   *
                   * No `onWritten`: the row is not in the session context and the screen re-reads
                   * itself once its write has left the queue.
                   *
                   * Keyed on the branch, because the row is the branch's.
                   */}
                  {/**
                   * `POS-008a`, `POS-006`, `CAT-015`: the three tenant policy numbers that had no
                   * writer at all. `tenant_settings_policies`, the owner alone, which is the row
                   * `PATCH /settings/policies` takes.
                   *
                   * First on this destination because the panels below it are narrower: a discount
                   * limit and a return window apply to every branch and every sale, where the three
                   * appointment panels are one branch's calendar.
                   */}
                  {permissions.has('tenant_settings_policies') ? (
                    <TenantPolicies canManage loadPolicies={loadPolicies} />
                  ) : null}
                  {permissions.has('tenant_settings_policies') ? (
                    <AppointmentHours
                      key={`appt-hours:${branch.id}`}
                      canManage={permissions.has('tenant_settings_policies')}
                      branchId={branch.id}
                      branchName={branch.name}
                      loadSchedule={loadAppointmentSchedule}
                    />
                  ) : null}
                  {/**
                   * §6.14 `APT-001`. Owner only: setting what the shop offers is policy, which is
                   * why `decisions/0039` added `GET branches/:branchId/bookable` for the five roles
                   * that may book rather than widening these routes.
                   *
                   * Keyed on the branch, because the rooms and people are the branch's and the
                   * types are the shop's: a branch switch has to re-read the first half.
                   */}
                  {permissions.has('tenant_settings_policies') ? (
                    <AppointmentOffers
                      key={`appt-offers:${branch.id}`}
                      canManage={permissions.has('tenant_settings_policies')}
                      branchId={branch.id}
                      branchName={branch.name}
                      loadTypes={loadAppointmentTypes}
                      loadResources={loadAppointmentResources}
                      loadVisitDates={loadVisitDates}
                      // `StaffRow` satisfies `StaffOption` structurally, so there is no second read
                      // and no adapter: offboarded staff come back flagged, not filtered.
                      loadStaff={loadStaff}
                    />
                  ) : null}
                  {/**
                   * §6.14 `APT-002`: the days off and the seasonal hours the slot search runs
                   * against. Owner only, and branch scoped like the panel above it.
                   */}
                  {permissions.has('tenant_settings_policies') ? (
                    <AppointmentCalendar
                      key={`appt-calendar:${branch.id}`}
                      canManage={permissions.has('tenant_settings_policies')}
                      branchId={branch.id}
                      branchName={branch.name}
                      // The branch's own today, not the device's. A bare date read as midnight UTC
                      // is the previous day west of UTC, which splits the lists at the wrong place.
                      today={branchDate(Date.now(), branch.timezone)}
                      timezone={branch.timezone}
                      loadClosures={loadAppointmentClosures}
                      loadOverrides={loadAppointmentOverrides}
                    />
                  ) : null}
                  {/* `ADM-005`. Owner only, `tenant_settings_policies`: the narrowest Appendix B row
                    that fits, since the matrix has none for granting support access. */}
                  {permissions.has('tenant_settings_policies') ? (
                    <SupportAccess timezone={branch.timezone} load={loadSupportAccess} />
                  ) : null}
                  {/**
                   * §6.13 `CLM-001` to `CLM-003`: the payer, its plans, what each plan pays and who
                   * is on it. Appendix B's `Panel account · manage`, owner and manager, which is
                   * the same two `staff_create_assign_role` gates this destination's rail entry on,
                   * so nobody who holds the row is shut out of the destination that carries it.
                   *
                   * **`report_financial` as well, because the list read is not this screen's
                   * route.** The chooser is `GET /panels`, which `StatementsController` owns on
                   * `report_financial`; `panel_account_manage` covers the panel read and all four
                   * writes. Both rows or the screen mounts and its first read answers 403, which a
                   * shopkeeper can do nothing about. Appendix B gives owner and manager both, so
                   * this costs nothing by default and holds when a tenant has edited its rows.
                   *
                   * This is the screen `../later_todo.md` 99 was waiting for: until it existed
                   * nothing in the product could create a panel, so `PanelStatements` below had a
                   * chooser with no options on all fifteen tenants.
                   */}
                  {permissions.has('panel_account_manage') &&
                  permissions.has('report_financial') ? (
                    <PanelAccounts
                      key={`panels:${branch.id}`}
                      loadPanels={loadPanels}
                      loadPanel={loadPanel}
                      searchCustomers={findCustomerMatches}
                      canManage={permissions.has('panel_account_manage')}
                      // The branch's own today, so a check date defaults to the day the shop is
                      // having rather than the device's.
                      today={branchDate(Date.now(), branch.timezone)}
                    />
                  ) : null}
                  {/* `TEN-006`. Owner only, `tenant_settings_policies`, the row the server takes. A
                    deployment with PIN sign-in off has no use for a terminal, so it is not offered
                    there either. */}
                  {permissions.has('tenant_settings_policies') && context.pinLength != null ? (
                    <Terminals
                      timezone={branch.timezone}
                      loadTerminals={loadTerminals}
                      onEnrol={(name, key) =>
                        enrolTerminal(
                          API_BASE_URL,
                          session,
                          { branchId: branch.id, name },
                          key,
                          fetchImpl,
                        )
                      }
                      hasLocalToken={terminalHere}
                      onUseHere={(token) => {
                        storeTerminalToken(token)
                        setTerminalHere(loadTerminalToken() !== null)
                      }}
                      onForgetHere={() => {
                        forgetTerminalToken()
                        setTerminalHere(false)
                      }}
                    />
                  ) : null}
                  {/* `OPS-206`, and `decisions/0026`: not a rail destination. Sending ANUBRIS a
                    note is a shop-level act and belongs beside the shop's own settings, and the
                    rail has used all twenty-six of its letters. Gated on nothing beyond the
                    session, like the route it posts to. */}
                  <Feedback />
                </>
              ) : null}

              {section === 'period-close' ? (
                <PeriodClose
                  key={branch.id}
                  loadClosed={loadClosedPeriods}
                  canClose={permissions.has('period_close')}
                  today={branchDate(Date.now(), branch.timezone)}
                  timezone={branch.timezone}
                />
              ) : null}

              {section === 'data-files' ? (
                <DataFiles
                  key={branch.id}
                  branchId={branch.id}
                  currency={branch.currency}
                  canImport={permissions.has('catalogue_bulk_import')}
                  canExport={permissions.has('customer_export')}
                  // The entity's own row, which `importPermissionsFor` asks for beside the import
                  // row: a customers file writes customer records, so the row that governs adding
                  // one by hand governs five thousand of them too.
                  canWriteCustomers={permissions.has('customer_create_edit')}
                  offerDownload={offerDownload}
                />
              ) : null}

              {section === 'commission' ? (
                <CommissionSchemes
                  key={branch.id}
                  load={loadCommissionSchemes}
                  branchId={branch.id}
                  // To name the branch a scheme from elsewhere belongs to, rather than print its id.
                  branches={context.branches}
                  // A new scheme is raised at this branch and takes its currency (`BR-01`). A
                  // listed version uses its own, because the list spans every branch this person
                  // can reach and the currency is fixed on the scheme.
                  currency={branch.currency}
                />
              ) : null}

              {section === 'outbox' ? (
                <Outbox
                  key={branch.id}
                  load={loadMessages}
                  timezone={branch.timezone}
                  onOpenCustomer={(customerId) => {
                    setScreenError(null)
                    setSection('customers')
                    openCustomerById(customerId, setScreenError)
                  }}
                />
              ) : null}

              {section === 'shipments' ? (
                <Shipments
                  key={branch.id}
                  branchId={branch.id}
                  currency={branch.currency}
                  timezone={branch.timezone}
                  loadShipment={loadShipment}
                  loadOrders={loadShipmentOrders}
                  loadOrder={loadShipmentOrder}
                  // The ordering row, not the landed cost one: Appendix B keeps them apart, and a
                  // button whose write comes back 403 stops the whole queue.
                  canOrder={permissions.has('purchase_order_raise')}
                  canAllocate={permissions.has('landed_cost_allocate')}
                />
              ) : null}

              {section === 'purchasing' ? (
                <Purchasing
                  timezone={branch.timezone}
                  key={branch.id}
                  load={loadPurchaseOrders}
                  loadLocations={loadStockLocations}
                  // The dock's row, not the screen's: the receipt endpoint takes `stock_receive`,
                  // and offering it to somebody without the row is a 403 that stops the queue.
                  canReceive={permissions.has('stock_receive')}
                  canOrder={permissions.has('purchase_order_raise')}
                />
              ) : null}

              {section === 'frame-catalogue' ? (
                <>
                  <FrameCatalogue
                    key={branch.id}
                    // Appendix B's `Catalogue - edit`. The screen says so rather than offering a
                    // Save that answers 403, which would halt the queue with everything behind it.
                    canEdit={permissions.has('catalogue_edit')}
                  />
                  {/**
                   * `CAT-010` and `CAT-012`, on the same section as frames rather than two more
                   * rail entries, because all 26 single-letter glyphs are taken and `rail.test.ts`
                   * enforces that they differ. It is also the right grouping: whoever unpacks a
                   * delivery of frames is who types the supplier's lens list.
                   */}
                  <LensCatalogue
                    key={`lens-${branch.id}`}
                    canEdit={permissions.has('catalogue_edit')}
                  />
                  <TreatmentCatalogue
                    key={`treatment-${branch.id}`}
                    canEdit={permissions.has('catalogue_edit')}
                  />
                  {/**
                   * §6.4 `CAT-020` to `CAT-022`. Beside the lens and treatment catalogues, for
                   * their reason: the rail has no free glyph, and whoever types a supplier's lens
                   * list is who types the contact lens one.
                   *
                   * `cl_product` was zero rows in all fourteen production tenants, because the API
                   * could create one and no screen could.
                   */}
                  <ClCatalogue
                    key={`cl-${branch.id}`}
                    // `catalogue_view`, which Appendix B gives every role: a dispenser serving a
                    // contact lens customer needs to see what is on file.
                    canView={permissions.has('catalogue_view')}
                    canEdit={permissions.has('catalogue_edit')}
                    loadProducts={loadClProducts}
                  />
                  {/**
                   * `PRC-010`. On the catalogue section because the lens list and its prices are
                   * entered from the same supplier sheet in the same sitting, and because the rail
                   * has no free glyph. Its own Appendix B row, which is narrower than the
                   * catalogue's: `price_book_edit_publish`.
                   */}
                  {/* `CAT-013`: the allow-list that makes a priced coating sellable. Between the
                      coating form and the price book, which is the order the work happens in. */}
                  {/* `CAT-004`. Its own frame picker, so the panel stands alone on this section
                      rather than needing a frame chosen somewhere else first. */}
                  <FrameImages
                    key={`images-${branch.id}`}
                    canEdit={permissions.has('catalogue_edit')}
                    loadFrames={() => readCatalogueFrames(reads)}
                    loadImages={(frameVariantId) => readFrameImages(reads, frameVariantId)}
                    loadImageBytes={(frameVariantId, imageId) =>
                      readFrameImageBytes(reads, frameVariantId, imageId)
                    }
                    upload={async (frameVariantId, body) => {
                      /**
                       * `decisions/0019`: the one write in the counter that does not go through the
                       * queue. A frame photograph is two to six megabytes and `OFF-001a`'s queue is
                       * IndexedDB on a cheap tablet, so forty frames at three angles would hold
                       * half a gigabyte of JPEG in browser storage.
                       */
                      await postDirect(reads, `/catalogue/variants/${frameVariantId}/images`, body)
                    }}
                    remove={async (frameVariantId, imageId) => {
                      await deleteDirect(
                        reads,
                        `/catalogue/variants/${frameVariantId}/images/${imageId}`,
                      )
                    }}
                  />
                  <LensTreatments
                    key={`compat-${branch.id}`}
                    canEdit={permissions.has('catalogue_edit')}
                    loadLenses={() => readCatalogueLenses(reads)}
                    loadTreatments={() => readCatalogueTreatments(reads)}
                    loadCompatibility={(lensProductId) => readLensTreatments(reads, lensProductId)}
                  />
                  <PriceBooks
                    key={`books-${branch.id}`}
                    canEdit={permissions.has('price_book_edit_publish')}
                    loadBooks={() => readPriceBooks(reads)}
                    loadLenses={() => readCatalogueLenses(reads)}
                  />
                  {/* `PRC-012`, and `decisions/0026`: no rail entry of its own. A preview of what
                    publishing a draft would do belongs beside the draft, and the rail has used all
                    twenty-six letters, so the next destination has to earn one rather than take it. */}
                  <PriceImpact load={loadPriceImpact} roundingSet={pricingRounding !== null} />
                </>
              ) : null}

              {section === 'suppliers-setup' ? (
                /*
                 * `mayOpenPriceList` again, and not only on the door: `forgetSession` clears
                 * `pricesFor`, and this is the second lock on the same thing, because a price list
                 * reachable past an absent door is the door not being absent.
                 */
                pricesFor !== null && mayOpenPriceList ? (
                  /**
                   * §6.19 `SUP-002`: one supplier's price list, which is what a purchase order is
                   * priced from. Instead of the supplier list rather than under it, because its own
                   * dismissal reads "Back to your suppliers".
                   *
                   * `cost_price_view` for the read and `purchase_order_raise` for the writes, the
                   * two rows the controller takes. Neither is enough on its own: `ROLE-001a` makes
                   * cost visibility a per-branch grant the service checks, which is why the screen
                   * has a wall the loader throws it into rather than a retry that cannot succeed.
                   */
                  <SupplierPrices
                    key={pricesFor.id}
                    supplier={{
                      id: pricesFor.id,
                      name: pricesFor.name,
                      currency: pricesFor.currency,
                    }}
                    canView={permissions.has('cost_price_view')}
                    canManage={permissions.has('purchase_order_raise')}
                    today={branchDate(Date.now(), branch.timezone)}
                    timezone={branch.timezone}
                    loadPrices={loadSupplierPrices}
                    onBack={() => {
                      setScreenError(null)
                      setPricesFor(null)
                    }}
                  />
                ) : (
                  <Suppliers
                    key={branch.id}
                    // The create and change routes take `purchase_order_raise`; the read is wider.
                    // Offering the buttons to a dispenser would be a 403, which halts the queue.
                    canManage={permissions.has('purchase_order_raise')}
                    /*
                     * **The door onto `SUP-002` is on the supplier row**, where a shopkeeper looks
                     * for it, rather than in a toolbar of every supplier's name underneath the
                     * list. The toolbar was interim and is gone: it existed only because
                     * `Suppliers.tsx` took no such prop, and it had to re-read the list through a
                     * wrapper to know the names.
                     *
                     * `mayOpenPriceList` is `cost_price_view` **and** `ROLE-001a`'s per-branch cost
                     * grant, so the button is absent rather than disabled for a role that would
                     * only meet `COST_NOT_VISIBLE` behind it. The screen checks both props, so
                     * passing one without the other shows nothing.
                     */
                    canOpenPrices={mayOpenPriceList}
                    onOpenPrices={(supplier) => {
                      setScreenError(null)
                      setPricesFor(supplier)
                    }}
                    loadSuppliers={loadSuppliers}
                  />
                )
              ) : null}

              {section === 'raise-order' ? (
                <RaisePurchaseOrder
                  key={branch.id}
                  branchId={branch.id}
                  // The chooser this screen could not have until `GET /suppliers` existed.
                  loadSuppliers={loadSuppliers}
                  // The branch's own currency as the default. There is no supplier read, so the
                  // supplier's invoicing currency cannot be shown, and every cost on the form is
                  // parsed at this currency's decimal places.
                  currency={branch.currency}
                />
              ) : null}

              {section === 'supplier-invoices' ? (
                <SupplierInvoice
                  key={branch.id}
                  load={loadPurchaseOrders}
                  loadOrder={loadPurchaseOrder}
                  // `IMP-004`'s base: what decides whether a rate is required or refused.
                  baseCurrency={branch.currency}
                  // W.5's manager, and a different Appendix B row from the screen's own: the
                  // accountant who raised the query is not the person who closes it.
                  canResolveDispute={permissions.has('purchase_order_approve')}
                  // `IMP-004`, `decisions/0041`. The 63rd Appendix B row, sensitive because it
                  // moves money. Without it the payment form is absent rather than disabled.
                  canSettle={permissions.has('supplier_invoice_settle')}
                />
              ) : null}

              {section === 'repairs' ? (
                <Repairs
                  timezone={branch.timezone}
                  key={branch.id}
                  load={loadRepairs}
                  currency={branch.currency}
                  onOpenCustomer={(customerId) => {
                    setScreenError(null)
                    setSection('customers')
                    openCustomerById(customerId, setScreenError)
                  }}
                />
              ) : null}

              {section === 'loan-frames' ? (
                <LoanFrames
                  key={branch.id}
                  load={loadLoanFrames}
                  loadOne={loadLoanFrame}
                  // `GET /repairs` with no stage, which is the server's open set: what is on the
                  // bench is what a frame can be lent against.
                  loadRepairs={loadRepairs}
                  loadLocations={loadStockLocations}
                  loadFrames={loadStockPositions}
                  currency={branch.currency}
                  timezone={branch.timezone}
                  // The branch's own today. A bare date is read as midnight UTC, so today is always
                  // before the moment the server records the issue and is always refused.
                  today={branchDate(Date.now(), branch.timezone)}
                  // `stock_adjust`, not the row that opened the screen: a write-off forfeits a
                  // customer's deposit, and a 403 stops the queue for everybody.
                  canWriteOff={permissions.has('stock_adjust')}
                  onOpenCustomer={(customerId) => {
                    // Neither the loan nor the repair carries a name, and a frame that has not come
                    // back is a phone call. The customer record is where the number is.
                    setScreenError(null)
                    setSection('customers')
                    openCustomerById(customerId, setScreenError)
                  }}
                />
              ) : null}

              {section === 'recalls' ? (
                <>
                  <Recalls
                    timezone={branch.timezone}
                    key={branch.id}
                    // `APT-004`: the run route takes a branch, and without this prop the screen
                    // withholds the button rather than posting a run the server would refuse.
                    branchId={branch.id}
                    load={loadRecalls}
                    onOpenCustomer={(customerId) => {
                      // The customer record is where a shopkeeper looks before ringing: their
                      // prescriptions, what they bought, and whether they consented to contact.
                      setScreenError(null)
                      setSection('customers')
                      openCustomerById(customerId, setScreenError)
                    }}
                  />
                  {/**
                   * §6.24 `APT-004`: what makes a customer due. Beside the queue it fills, because
                   * a rule with no queue is nothing to look at and a queue with no rule is empty
                   * for a reason only this panel can explain.
                   *
                   * **A narrower row than the section.** The section is `recall_run_campaign`
                   * (owner, manager, dispenser) and every rules route takes
                   * `tenant_settings_policies`, the owner alone, so a manager who may run a
                   * campaign sees the queue and not the rules. Gated on the row the server checks
                   * rather than on the section's, because a Save that answers 403 is a refusal and
                   * a refusal blocks the head of the write queue.
                   *
                   * No `onOpenRecalls`: the queue it would navigate to is the panel above this one.
                   */}
                  {permissions.has('tenant_settings_policies') ? (
                    <RecallRules
                      canManage={permissions.has('tenant_settings_policies')}
                      loadRules={loadRecallRules}
                    />
                  ) : null}
                </>
              ) : null}

              {section === 'notifications' ? (
                <Notifications key={branch.id} load={loadNotifications} />
              ) : null}

              {section === 'reports' ? (
                <>
                  <Reports
                    key={branch.id}
                    loadListing={loadReportListing}
                    runReport={runReport}
                    branchId={branch.id}
                    timezone={branch.timezone}
                  />
                  {/**
                   * Section 15.20 step 4, `CLM-001` to `CLM-006`: the monthly statement a panel is
                   * sent. `report_financial`, which is the row `StatementsController` checks on all
                   * three of its routes and the row `reports/panel_statement` already asked for.
                   * Appendix B grows by nothing for this one: a statement is the Appendix Z
                   * "Panel statement" report frozen on the day it was sent, so a row of its own
                   * would say the same thing twice. The three panels beside it on this
                   * destination and on `setup` did need rows and have them.
                   *
                   * **A panel under the reports listing rather than a rail destination**, because
                   * all twenty-six glyphs are taken and `rail.test.ts` enforces it
                   * (`decisions/0026`, `decisions/0040`). This is the right panel anyway: the rail
                   * destination is gated on `report_operational`, which every role holds, and this
                   * is the one thing in the section a dispenser may not see.
                   *
                   * **The gate is load-bearing, not decoration.** `PanelStatements` registers its
                   * `loadPanels` effect before `if (!canIssue) return null`, so mounted ungated it
                   * would fire `GET /panels` for a cashier and take a 403 on a read nobody asked
                   * for. `app-reachability.test.tsx` asserts the call never happens, not merely
                   * that nothing renders.
                   */}
                  {permissions.has('report_financial') ? (
                    <PanelStatements
                      key={`statements:${branch.id}`}
                      loadPanels={loadPanels}
                      loadStatements={loadPanelStatements}
                      canIssue={permissions.has('report_financial')}
                      today={branchDate(Date.now(), branch.timezone)}
                      timezone={branch.timezone}
                    />
                  ) : null}
                  {/**
                   * §6.13 `CLM-004`: the cheque a panel sends, against its open claims.
                   *
                   * Beside the statement because that is the document this money is paid against,
                   * and on a narrower row: `panel_remittance_record` is sensitive and
                   * `report_financial` is not, because reading what a panel owes is not the
                   * authority to write what it paid. Both are needed here for the reason the panel
                   * on `setup` needs two: the chooser is `GET /panels` on `report_financial` and
                   * the ageing is `GET /claims/aged` on `panel_remittance_record`. Appendix B gives
                   * owner, manager and accountant both rows, so the pair costs nothing by default.
                   *
                   * This is the credit end of `insurance_receivable`. Every panel-tendered sale has
                   * debited that account since the ledger was written and nothing credited it, so a
                   * panel that had paid in full still read as owing the lot on the balance sheet
                   * (`../later_todo.md` 98). Recording a remittance posts the other half.
                   */}
                  {permissions.has('panel_remittance_record') &&
                  permissions.has('report_financial') ? (
                    <PanelRemittance
                      key={`remittance:${branch.id}`}
                      loadPanels={loadPanels}
                      loadAged={loadAged}
                      canRecord={permissions.has('panel_remittance_record')}
                      today={branchDate(Date.now(), branch.timezone)}
                    />
                  ) : null}
                </>
              ) : null}

              {section === 'cash' ? (
                terminalId === null ? (
                  <p className="lm-empty">{t('app.noTerminal')}</p>
                ) : (
                  <>
                    <CashSession
                      branchId={branch.id}
                      terminalId={terminalId}
                      {...(cashSession ? { session: cashSession } : {})}
                      // Re-read when the server has the write, not when the queue does. Without this
                      // the banner above kept reading "Drawer closed" over a drawer that was open, and
                      // `TakePayment` refused cash into it.
                      onSaved={() => {
                        void refreshCashSession()
                      }}
                      // Both, because the row alone is not enough: `approveVariance` refuses the
                      // person who counted with a 403, which is permanent, so a manager who counted
                      // their own drawer would queue a refused write and stop the till a second way.
                      canApproveVariance={permissions.has('cash_variance_approve')}
                      staffId={context.staff.id}
                    />
                    {/**
                     * §6.27 `BR-11`: recording what the shop spent. `expenses_record`, which
                     * Appendix B gives the owner, manager, cashier and accountant.
                     *
                     * **A panel rather than a rail destination, because there is no letter left.**
                     * All twenty-six glyphs are taken and `rail.test.ts` enforces it, so
                     * `decisions/0026` and `decisions/0040` settled that the next screen becomes a
                     * panel instead of taking one. Under the drawer is the right panel anyway: a
                     * cost paid from the drawer is an expense and a cash pay-out in the same
                     * breath, so the figure it changes is the expected cash on the screen above it.
                     *
                     * `drawerOpen` from the session the shell already holds, because cash is not
                     * offered as a source without an open drawer: the server refuses it, and a
                     * refusal is a 422 at the head of the write queue.
                     */}
                    {mayRecordExpenses ? (
                      <Expenses
                        key={`expenses:${branch.id}`}
                        branchId={branch.id}
                        terminalId={terminalId}
                        currency={branch.currency}
                        timezone={branch.timezone}
                        drawerOpen={cashSession?.status === 'open'}
                        expenses={expenses}
                        onSaved={() => {
                          void refreshExpenses()
                        }}
                      />
                    ) : null}
                  </>
                )
              ) : null}
            </>
          ) : null}
        </Shell>
      </WriteQueueProvider>
    </LocaleProvider>
  )
}
